import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import moment from "moment";
import { Offcanvas } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import {
  FaBars,
  FaBuilding,
  FaChartBar,
  FaChartPie,
  FaChevronRight,
  FaDatabase,
  FaFileAlt,
  FaFilter,
  FaHandHoldingUsd,
  FaHandshake,
  FaInfoCircle,
  FaList,
  FaUser,
  FaUsers,
} from "react-icons/fa";
import { RootState } from "../../../core/data/redux/store";
import { fetchUsersByClientId, User } from "../../../api/todo";
import {
  CompanyResponse,
  fetchCompaniesByUserId,
  RawCompany,
} from "../../../api/contact";
import { getLeadsByUserId, Lead } from "../../../api/lead";
import { Deal, fetchDealsByUserId } from "../../../api/deal";
import {
  fetchProposalsByUserId,
  ProposalResponse,
} from "../../../api/proposal";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import ScrollToTop from "../../../core/common/ScrollToTop";
import Tip from "../../../core/common/tooltip";
import CountUp from "react-countup";
import { useNavigate } from "react-router";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#A569BD",
  "#58D68D",
  "#E74C3C",
  "#3498DB",
  "#F39C12",
  "#2ECC71",
];

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const clientId = useSelector((state: RootState) => state.auth.client_id);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [contacts, setContacts] = useState<CompanyResponse[]>([]);
  const [leadDateRange, setLeadDateRange] = useState<
    "today" | "week" | "month" | "custom"
  >("month");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [contactRange, setContactRange] = useState<
    "today" | "week" | "month" | "custom"
  >("month");

  const [selectedContact, setSelectedContact] = useState<RawCompany | null>(
    null,
  );
  const [showContactModal, setShowContactModal] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [proposals, setProposals] = useState<ProposalResponse[]>([]);
  const [filter, setFilter] = useState<
    "all" | "last7days" | "thismonth" | "last3months"
  >("all");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmployeeSidebar, setShowEmployeeSidebar] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any | null>(null);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<any | null>(null);
  const [selectedProposal, setSelectedProposal] = useState<any | null>(null);
  const [showProposalModal, setShowProposalModal] = useState(false);

  const openProposalModal = (proposal: any) => {
    setSelectedProposal(proposal);
    setShowProposalModal(true);
  };

  const closeProposalModal = () => {
    setShowProposalModal(false);
    setSelectedProposal(null);
  };

  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      try {
        const userList = await fetchUsersByClientId(clientId || "");
        setUsers(userList);
        if (userList.length > 0) {
          setSelectedUserId(userList[0].id);
        }
      } catch (err) {
        setError("Failed to load employees. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    loadUsers();
  }, [clientId]);

  useEffect(() => {
    if (!selectedUserId) return;

    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [contactsData, leadsData, dealsData, proposalsData] =
          await Promise.all([
            fetchCompaniesByUserId(selectedUserId),
            getLeadsByUserId(selectedUserId),
            fetchDealsByUserId(selectedUserId),
            fetchProposalsByUserId(selectedUserId),
          ]);
        // Use this version – it's safe, readable, handles your real data structure
setContacts(
  contactsData.filter(c => 
    c.company.leadowner_id === selectedUserId || 
    (c.company.users && typeof c.company.users === 'object' && (c.company.users as any).id === selectedUserId)
  )
);
setLeads(leadsData.filter(l => l.users?.id === selectedUserId));
setDeals(dealsData.filter(d => d.leads?.users?.id === selectedUserId));
setProposals(proposalsData.filter(p => p.leads?.contact?.users?.id === selectedUserId));
      } catch (err) {
        setError("Failed to load dashboard data. Please refresh.");
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [selectedUserId]);
  const filteredUsers = useMemo(
    () =>
      users.filter(
        (u) =>
          u.name.toLowerCase().includes(employeeSearch) ||
          u.email?.toLowerCase().includes(employeeSearch),
      ),
    [users, employeeSearch],
  );

  const currentDate = moment("2026-01-02");

  const applyFilter = (items: any[], dateKey: string) => {
    if (filter === "all") return items;

    let startDate: moment.Moment;

    if (filter === "last7days") {
      startDate = currentDate.clone().subtract(7, "days");
    } else if (filter === "thismonth") {
      startDate = currentDate.clone().startOf("month");
    } else {
      startDate = currentDate.clone().subtract(3, "months").startOf("month");
    }

    return items.filter((item) => {
      const itemDate = moment(item[dateKey]);
      return itemDate.isValid() && itemDate.isAfter(startDate);
    });
  };

  // Contacts Processing
  const filteredContacts = applyFilter(contacts, "company.created_at");
  const totalContacts = filteredContacts.length;
  const recentContacts = filteredContacts
    .sort(
      (a, b) =>
        moment(b.company.created_at).unix() -
        moment(a.company.created_at).unix(),
    )
    .slice(0, 5);

  const filterContactsByRange = (items: CompanyResponse[]) => {
    const now = moment();

    return items.filter((item) => {
      const created = moment(item.company.created_at);
      if (!created.isValid()) return false;

      switch (leadDateRange) {
        case "today":
          return created.isSame(now, "day");
        case "week":
          return created.isAfter(now.clone().subtract(7, "days"));
        case "month":
          return created.isAfter(now.clone().subtract(30, "days"));
        default:
          return true;
      }
    });
  };

  const rangedRecentContacts = useMemo(
    () => filterContactsByRange(recentContacts),
    [recentContacts, contactRange],
  );
  const rangedContacts = filterContactsByRange(filteredContacts);
  const leadSourceCounts = rangedContacts.reduce<Record<string, number>>(
    (acc, item) => {
      const source = item.company.leadsource_name || "Unknown";
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    },
    {},
  );

  const leadSourceData = Object.entries(leadSourceCounts).map(
    ([name, value]) => ({ name, value }),
  );
  const leadSourceTooltip = ({ active, payload }: any) => {
    if (active && payload?.length) {
      const { name, value } = payload[0].payload;
      return (
        <div className="bg-white p-2 border rounded shadow-sm small">
          <strong>{name}</strong>
          <div>{value} contacts</div>
        </div>
      );
    }
    return null;
  };

  const sortedSources = Object.entries(leadSourceCounts).sort(
    (a, b) => b[1] - a[1],
  );
  const highSources = sortedSources.slice(0, 3).map(([name]) => name);
  const lowSources = sortedSources.slice(-3).map(([name]) => name);
  const avgContactsPerSource =
    totalContacts / Object.keys(leadSourceCounts).length || 0;

  // Leads Processing
  const filteredLeads = applyFilter(leads, "created_at");
  const totalLeads = filteredLeads.length;
  const recentLeads = filteredLeads
    .sort((a, b) => moment(b.created_at).unix() - moment(a.created_at).unix())
    .slice(0, 10);

  const leadAgeDays = filteredLeads.map((lead) =>
    currentDate.diff(moment(lead.created_at), "days"),
  );
  const avgLeadAge =
    leadAgeDays.reduce((sum, age) => sum + age, 0) / totalLeads || 0;

  const statusCounts: Record<string, number> = filteredLeads.reduce(
    (acc, lead) => {
      const status = lead.lead_status || "Unknown";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {},
  );
  const statusData = Object.entries(statusCounts).map(([name, value]) => ({
    name,
    value,
  }));
  const sortedStatuses = Object.entries(statusCounts).sort(
    (a, b) => b[1] - a[1],
  );
  const highStatuses = sortedStatuses.slice(0, 3).map(([name]) => name);
  const lowStatuses = sortedStatuses.slice(-3).map(([name]) => name);

  const leadSourceLeadCounts: Record<string, number> = filteredLeads.reduce(
    (acc, lead) => {
      const source = lead.leadsource?.LeadSource_name || "Unknown";
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    },
    {},
  );
  const leadSourceLeadData = Object.entries(leadSourceLeadCounts).map(
    ([name, value]) => ({ name, value }),
  );

  const leadStageCounts = filteredLeads.reduce(
    (acc, lead) => {
      const stage = lead.leadstage?.LeadStage_name || "Unknown";
      acc[stage] = (acc[stage] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );
  const leadStageData = Object.entries(leadStageCounts).map(
    ([name, value]) => ({ name, value }),
  );

  const leadTypeCounts = filteredLeads.reduce(
    (acc, lead) => {
      const type = lead.leadtype?.leadtype_name || "Unknown";
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );
  const leadTypeData = Object.entries(leadTypeCounts).map(([name, value]) => ({
    name,
    value,
  }));

  const leadIndustryCounts = filteredLeads.reduce(
    (acc, lead) => {
      const industry = lead.leadindustry?.LeadIndustry_name || "Unknown";
      acc[industry] = (acc[industry] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );
  const leadIndustryData = (
    Object.entries(leadIndustryCounts) as [string, number][]
  )
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, value]) => ({ name, value }));

  const leadproductCounts = filteredLeads.reduce(
    (acc, lead) => {
      const product = lead.products?.product_name || "None";
      acc[product] = (acc[product] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );
  const productData = Object.entries(leadproductCounts).map(
    ([name, value]) => ({ name, value }),
  );

  const productTypeCounts = filteredLeads.reduce(
    (acc, lead) => {
      const pt = lead.producttype?.product_name || "None";
      acc[pt] = (acc[pt] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );
  const productTypeData = Object.entries(productTypeCounts).map(
    ([name, value]) => ({ name, value }),
  );

  const customerTypeCounts = filteredLeads.reduce(
    (acc, lead) => {
      const ct = lead.customertypes?.customerPotential_name || "Unknown";
      acc[ct] = (acc[ct] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );
  const customerTypeData = Object.entries(customerTypeCounts).map(
    ([name, value]) => ({ name, value }),
  );

  // Deals Processing
  const filteredDeals = applyFilter(deals, "created_at");
  const totalDeals = filteredDeals.length;
  const recentDeals = filteredDeals
    .sort((a, b) => moment(b.created_at).unix() - moment(a.created_at).unix())
    .slice(0, 10);

  const dealStatusCounts: Record<string, number> = filteredDeals.reduce(
    (acc, deal) => {
      const status = deal.status || "Unknown";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {},
  );
  const dealStatusData = Object.entries(dealStatusCounts).map(
    ([name, value]) => ({ name, value }),
  );
  const sortedDealStatuses = Object.entries(dealStatusCounts).sort(
    (a, b) => b[1] - a[1],
  );
  const highDealStatuses = sortedDealStatuses.slice(0, 3).map(([name]) => name);
  const lowDealStatuses = sortedDealStatuses.slice(-3).map(([name]) => name);

  const priorityCounts: Record<string, number> = filteredDeals.reduce(
    (acc, deal) => {
      const priority = deal.deal_priority || "Unknown";
      acc[priority] = (acc[priority] || 0) + 1;
      return acc;
    },
    {},
  );
  const priorityData = Object.entries(priorityCounts).map(([name, value]) => ({
    name,
    value,
  }));

  const dealSourceCounts: Record<string, number> = filteredDeals.reduce(
    (acc, deal) => {
      const source = deal.dealsource?.deal_source_nema || "Unknown";
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    },
    {},
  );
  const dealSourceData = Object.entries(dealSourceCounts).map(
    ([name, value]) => ({ name, value }),
  );
  const sortedDealSources = Object.entries(dealSourceCounts).sort(
    (a, b) => b[1] - a[1],
  );
  const highDealSources = sortedDealSources.slice(0, 3).map(([name]) => name);
  const lowDealSources = sortedDealSources.slice(-3).map(([name]) => name);

  const calculateClosedValue = (
    range: "last7days" | "thismonth" | "last3months",
  ) => {
    const filterDealsByRange = (days: number) => {
      const start = currentDate.clone().subtract(days, "days");
      return deals.filter(
        (d) =>
          moment(d.created_at).isAfter(start) &&
          ["Won", "Closed"].includes(d.status),
      );
    };

    let closedDeals: Deal[];
    if (range === "last7days") {
      closedDeals = filterDealsByRange(7);
    } else if (range === "thismonth") {
      closedDeals = deals.filter(
        (d) =>
          moment(d.created_at).month() === currentDate.month() &&
          moment(d.created_at).year() === currentDate.year() &&
          ["Won", "Closed"].includes(d.status),
      );
    } else {
      closedDeals = filterDealsByRange(90);
    }
    return closedDeals.reduce(
      (sum, deal) => sum + parseFloat(deal.deal_value || "0"),
      0,
    );
  };

  // Proposals Processing
  const filteredProposals = applyFilter(proposals, "created_at");
  const totalProposals = filteredProposals.length;
  const recentProposals = filteredProposals
    .sort((a, b) => moment(b.created_at).unix() - moment(a.created_at).unix())
    .slice(0, 10);

  const proposalStatusCounts: Record<string, number> = filteredProposals.reduce(
    (acc, prop) => {
      const status = prop.status || "Unknown";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {},
  );
  const proposalStatusData = Object.entries(proposalStatusCounts).map(
    ([name, value]) => ({ name, value }),
  );
  const sortedProposalStatuses = Object.entries(proposalStatusCounts).sort(
    (a, b) => b[1] - a[1],
  );
  const highProposalStatuses = sortedProposalStatuses
    .slice(0, 3)
    .map(([name]) => name);
  const lowProposalStatuses = sortedProposalStatuses
    .slice(-3)
    .map(([name]) => name);

  const upcomingFollowups = filteredProposals
    .filter((prop) => moment(prop.validation_date).isAfter(currentDate))
    .sort(
      (a, b) =>
        moment(a.validation_date).unix() - moment(b.validation_date).unix(),
    )
    .slice(0, 5);

  const productCounts: Record<string, number> = filteredProposals.reduce(
    (acc, prop) => {
      const product = prop.product_name || "Unknown";
      acc[product] = (acc[product] || 0) + 1;
      return acc;
    },
    {},
  );
  const topProducts = Object.entries(productCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({ name, count }));

  const selectedUser = users.find((u) => u.id === selectedUserId);

  const renderTooltip = (content: string) => (
    <Tip text={content}>
      <i className="fa fa-info-circle text-muted fs-14" />
    </Tip>
  );

  const customChartTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border rounded shadow-sm">
          <p className="mb-0">
            <strong>{label}</strong>: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <PageLoadProgressBar />
      <div className="page-wrapper">
        <div className="container-fluid p-0">
          <Button
            variant="light"
            className="position-fixed top-0 start-0 m-3 z-3 shadow-sm"
            onClick={() => setShowEmployeeSidebar(true)}
            style={{ zIndex: 1030 }}
          >
            <FaBars size={24} />
          </Button>
          {/* Main content - now takes full width */}
          <Row className="g-0">
            <Col className="p-4 flex-grow-1" style={{ minHeight: "100vh" }}>
              {error && (
                <Alert
                  variant="danger"
                  dismissible
                  onClose={() => setError(null)}
                  className="shadow-sm rounded-3"
                >
                  <div className="d-flex align-items-center gap-2">
                    <i className="fa fa-exclamation-triangle" />
                    <span>{error}</span>
                  </div>
                </Alert>
              )}
              {!selectedUserId ? (
                <div className="d-flex justify-content-center py-5">
                  <div className="text-center text-muted">
                    <div className="mb-3">
                      <i className="fa fa-user-circle fa-3x opacity-25" />
                    </div>
                    <h6 className="fw-semibold text-dark mb-1">
                      No Employee Selected
                    </h6>
                    <p className="mb-0 small">
                      Select an employee from the list to view their dashboard
                    </p>
                  </div>
                </div>
              ) : isLoading ? (
                <div className="d-flex justify-content-center py-5">
                  <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <div className="mt-3 fw-medium text-muted">
                      Loading dashboard data…
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <Row className="mb-4 align-items-center g-3">
                    {/* LEFT : Title */}
                    <Col>
                      <div className="d-flex align-items-center gap-3">
                        {/* Sidebar toggle */}
                        <Button
                          variant="light"
                          className="rounded-circle shadow-sm"
                          onClick={() => setShowEmployeeSidebar(true)}
                        >
                          <FaBars />
                        </Button>

                        {/* Title block */}
                        <div>
                          <h4 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                            <FaDatabase className="text-primary" />
                            {selectedUser?.name}'s Dashboard
                          </h4>
                          <small className="text-muted">
                            Performance overview & activity insights
                          </small>
                        </div>
                      </div>
                    </Col>

                    {/* RIGHT : Filters */}
                    <Col md="auto">
                      <div className="d-flex align-items-center gap-3">
                        {/* Time context badge */}
                        <span className="badge bg-light text-muted px-3 py-2">
                          Viewing:{" "}
                          {
                            {
                              all: "All Time",
                              last7days: "Last 7 Days",
                              thismonth: "This Month",
                              last3months: "Last 3 Months",
                            }[filter]
                          }
                        </span>

                        {/* Filter select */}
                        <Form.Select
                          value={filter}
                          onChange={(e) => setFilter(e.target.value as any)}
                          className="shadow-sm"
                          style={{ minWidth: 170 }}
                        >
                          <option value="all">All Time</option>
                          <option value="last7days">Last 7 Days</option>
                          <option value="thismonth">This Month</option>
                          <option value="last3months">Last 3 Months</option>
                        </Form.Select>
                      </div>
                    </Col>
                  </Row>

                  {/* Summary Metrics - Enhanced with animations or just modern cards */}
                  <Row className="mb-5 g-4">
                    {/* Total Contacts */}
                    <Col xs={6} md={6} lg={3}>
                      <Tip
                        text={`Total Contacts: ${totalContacts.toLocaleString()}`}
                      >
                        <div className="dashboard-card h-100 border-0 shadow-lg hover-lift transition-all">
                          <div className="card-content p-4 d-flex flex-column align-items-center text-center">
                            <div className="icon-circle bg-primary-subtle text-primary mb-3">
                              <FaUsers size={32} />
                            </div>

                            <h6 className="text-muted mb-1 fw-medium">
                              Total Contacts
                            </h6>

                            <h3 className="fw-bold text-primary mb-0">
                              <CountUp
                                end={totalContacts}
                                duration={2}
                                separator=","
                              />
                            </h3>
                          </div>
                        </div>
                      </Tip>
                    </Col>

                    {/* Total Leads */}
                    <Col xs={6} md={6} lg={3}>
                      <Tip text={`Total Leads: ${totalLeads.toLocaleString()}`}>
                        <div className="dashboard-card h-100 border-0 shadow-lg hover-lift transition-all">
                          <div className="card-content p-4 d-flex flex-column align-items-center text-center">
                            <div className="icon-circle bg-success-subtle text-success mb-3">
                              <FaChartBar size={32} />
                            </div>

                            <h6 className="text-muted mb-1 fw-medium">
                              Total Leads
                            </h6>

                            <h3 className="fw-bold text-success mb-0">
                              <CountUp
                                end={totalLeads}
                                duration={2}
                                separator=","
                              />
                            </h3>
                          </div>
                        </div>
                      </Tip>
                    </Col>

                    {/* Total Deals */}
                    <Col xs={6} md={6} lg={3}>
                      <Tip text={`Total Deals: ${totalDeals.toLocaleString()}`}>
                        <div className="dashboard-card h-100 border-0 shadow-lg hover-lift transition-all">
                          <div className="card-content p-4 d-flex flex-column align-items-center text-center">
                            <div className="icon-circle bg-warning-subtle text-warning mb-3">
                              <FaHandshake size={32} />
                            </div>

                            <h6 className="text-muted mb-1 fw-medium">
                              Total Deals
                            </h6>

                            <h3 className="fw-bold text-warning mb-0">
                              <CountUp
                                end={totalDeals}
                                duration={2}
                                separator=","
                              />
                            </h3>
                          </div>
                        </div>
                      </Tip>
                    </Col>

                    {/* Total Proposals */}
                    <Col xs={6} md={6} lg={3}>
                      <Tip
                        text={`Total Proposals: ${totalProposals.toLocaleString()}`}
                      >
                        <div className="dashboard-card h-100 border-0 shadow-lg hover-lift transition-all">
                          <div className="card-content p-4 d-flex flex-column align-items-center text-center">
                            <div className="icon-circle bg-info-subtle text-info mb-3">
                              <FaFileAlt size={32} />
                            </div>

                            <h6 className="text-muted mb-1 fw-medium">
                              Total Proposals
                            </h6>

                            <h3 className="fw-bold text-info mb-0">
                              <CountUp
                                end={totalProposals}
                                duration={2}
                                separator=","
                              />
                            </h3>
                          </div>
                        </div>
                      </Tip>
                    </Col>
                  </Row>

                  {/* Contacts Section - Enhanced */}
                  <Card className="mb-5 border-0 rounded-4 overflow-hidden shadow-sm">
                    <Card.Header className="bg-primary text-white d-flex align-items-center justify-content-between py-3 px-4">
                      <div className="d-flex align-items-center">
                        <FaUsers className="me-3" size={24} />
                        <h5 className="mb-0 fw-semibold">Contacts Overview</h5>
                      </div>

                      <Tip text="Key metrics and distribution of contacts created by the selected employee">
                        <i className="fa fa-info-circle text-muted fs-14" />
                      </Tip>
                    </Card.Header>

                    <Card.Body className="p-4 p-lg-5 bg-white">
                      <Row className="g-4">
                        {/* Lead Source Distribution */}
                        <Col lg={12}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* Header */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-primary-subtle rounded-circle p-2 me-3">
                                  <FaChartPie
                                    className="text-primary"
                                    size={20}
                                  />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Lead Source Distribution
                                  </h6>
                                  <small className="text-muted">
                                    How contacts are coming in
                                  </small>
                                </div>
                              </div>

                              <div className="d-flex align-items-center gap-3">
                                <Form.Select
                                  size="sm"
                                  value={leadDateRange}
                                  onChange={(e) =>
                                    setLeadDateRange(e.target.value as any)
                                  }
                                  className="w-auto fs-11"
                                >
                                  <option value="today">Today</option>
                                  <option value="week">Last 7 days</option>
                                  <option value="month">Last 30 days</option>
                                </Form.Select>

                                <Tip text="Shows how contacts are distributed across lead sources">
                                  <i className="fa fa-info-circle text-muted fs-14" />
                                </Tip>
                              </div>
                            </Card.Header>

                            <Card.Body className="p-4 p-lg-5">
                              {leadSourceData.length > 0 ? (
                                <>
                                  {/* 🔹 KPI STRIP (Super Admin style) */}
                                  <Row className="g-3 mb-4">
                                    <Col md={4}>
                                      <div className="p-3 rounded-3 bg-primary-subtle text-center dashboard-card">
                                        <div className="small text-primary fw-medium">
                                          Total Contacts
                                        </div>
                                        <div className=" fw-bold text-dark">
                                          {totalContacts}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col md={4}>
                                      <div className="p-3 rounded-3 bg-success-subtle text-center dashboard-card">
                                        <div className="small text-success fw-medium">
                                          Top Source
                                        </div>
                                        <div className="fw-bold text-dark text-truncate">
                                          {highSources[0] || "—"}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col md={4}>
                                      <div className="p-3 rounded-3 bg-info-subtle text-center dashboard-card">
                                        <div className="small text-info fw-medium">
                                          Avg / Source
                                        </div>
                                        <div className="fw-bold text-dark">
                                          {avgContactsPerSource.toFixed(1)}
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>

                                  {/* 🔹 CHART */}
                                  <div
                                    className="position-relative"
                                    style={{ height: 360 }}
                                  >
                                    <ResponsiveContainer>
                                      <PieChart>
                                        <Pie
                                          data={leadSourceData}
                                          dataKey="value"
                                          nameKey="name"
                                          cx="50%"
                                          cy="50%"
                                          innerRadius={90}
                                          outerRadius={140}
                                          paddingAngle={4}
                                          labelLine={false}
                                          isAnimationActive
                                          animationBegin={150}
                                          animationDuration={800}
                                          animationEasing="ease-out"
                                          onClick={(_, index) => {
                                            setSelectedSource(
                                              leadSourceData[index].name,
                                            );
                                            setShowSourceModal(true);
                                          }}
                                        >
                                          {leadSourceData.map((_, index) => (
                                            <Cell
                                              key={index}
                                              fill={
                                                COLORS[index % COLORS.length]
                                              }
                                              stroke="#fff"
                                              strokeWidth={2}
                                            />
                                          ))}
                                        </Pie>

                                        <RechartsTooltip
                                          content={leadSourceTooltip}
                                          cursor={{
                                            fill: "rgba(99,102,241,0.08)",
                                          }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>

                                    {/* Center KPI */}
                                    <div className="position-absolute top-50 start-50 translate-middle text-center">
                                      <div className="fw-bold text-dark">
                                        {totalContacts}
                                      </div>
                                      <div className="small text-muted">
                                        Total Contacts
                                      </div>
                                    </div>
                                  </div>

                                  {/* 🔹 RANKED LIST */}
                                  <div className="mt-4">
                                    {sortedSources
                                      .slice(0, 5)
                                      .map(([name, count], index) => (
                                        <div
                                          key={name}
                                          className="d-flex align-items-center justify-content-between mb-2 small ranked-row"
                                        >
                                          <div className="d-flex align-items-center gap-2">
                                            <span
                                              className="rounded-circle"
                                              style={{
                                                width: 10,
                                                height: 10,
                                                backgroundColor:
                                                  COLORS[index % COLORS.length],
                                              }}
                                            />
                                            <span className="fw-medium text-dark">
                                              {name}
                                            </span>
                                          </div>
                                          <span className="text-muted">
                                            {count}
                                          </span>
                                        </div>
                                      ))}
                                  </div>

                                  {/* 🔹 INSIGHT */}
                                  <div className="text-center mt-4 small text-muted">
                                    <strong>{highSources[0]}</strong>{" "}
                                    contributes{" "}
                                    <span className="fw-semibold text-primary">
                                      {(
                                        ((leadSourceCounts[highSources[0]] ||
                                          0) /
                                          totalContacts) *
                                        100
                                      ).toFixed(1)}
                                      %
                                    </span>{" "}
                                    of all contacts
                                  </div>
                                </>
                              ) : (
                                /* Empty State */
                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                                  <FaDatabase
                                    size={56}
                                    className="opacity-25 mb-4"
                                  />
                                  <h6 className="mb-2 fw-medium text-dark">
                                    No data available yet
                                  </h6>
                                  <p className="mb-0 small">
                                    Contact sources will appear once data is
                                    collected
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Modal
                          show={showSourceModal}
                          onHide={() => setShowSourceModal(false)}
                          size="lg"
                          centered
                        >
                          <Modal.Header closeButton>
                            <Modal.Title>
                              Contacts from “{selectedSource}”
                            </Modal.Title>
                          </Modal.Header>

                          <Modal.Body>
                            <Table hover responsive className="mb-0">
                              <thead>
                                <tr>
                                  <th>Company</th>
                                  <th>Contact</th>
                                  <th>Email</th>
                                  <th>Created</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rangedContacts
                                  .filter(
                                    (c) =>
                                      c.company.leadsource_name ===
                                      selectedSource,
                                  )
                                  .map((c) => (
                                    <tr key={c.company.contact_id}>
                                      <td>{c.company.company_name}</td>
                                      <td>{c.company.contact_person_name}</td>
                                      <td>{c.company.company_mail || "—"}</td>
                                      <td>
                                        {moment(c.company.created_at).format(
                                          "MMM DD, YYYY",
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </Table>
                          </Modal.Body>
                        </Modal>

                        {/* Recent Contacts */}
                        <Col lg={12}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* Header */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-primary-subtle rounded-circle p-2 me-3">
                                  <FaList className="text-primary" size={20} />
                                </div>
                                <h6 className="mb-0 fw-semibold text-dark">
                                  Recent Contacts (Top 10)
                                </h6>
                              </div>

                              <div className="d-flex align-items-center gap-3">
                                <Form.Select
                                  size="sm"
                                  value={contactRange}
                                  onChange={(e) =>
                                    setContactRange(e.target.value as any)
                                  }
                                  className="w-auto fs-11"
                                >
                                  <option value="today">Today</option>
                                  <option value="week">Last 7 days</option>
                                  <option value="month">Last 30 days</option>
                                </Form.Select>

                                <Tip text="Most recently created contacts in the selected period">
                                  <i className="fa fa-info-circle text-muted fs-14" />
                                </Tip>
                              </div>
                            </Card.Header>

                            {/* Table */}
                            <div className="table-responsive">
                              <Table
                                hover
                                className="mb-0 table-borderless recent-contacts-table"
                              >
                                <thead className="bg-light-subtle">
                                  <tr>
                                    <th className="ps-4 py-3 fw-semibold">
                                      Company
                                    </th>
                                    <th className="py-3 fw-semibold">
                                      Contact Person
                                    </th>
                                    <th className="py-3 fw-semibold">Email</th>
                                    <th className="py-3 fw-semibold">Phone</th>
                                    <th className="py-3 fw-semibold">Source</th>
                                    <th className="py-3 fw-semibold text-center">
                                      Created
                                    </th>
                                    <th className="py-3 fw-semibold text-center">
                                      Action
                                    </th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {rangedRecentContacts
                                    .slice(0, 10)
                                    .map((contact) => {
                                      const company = contact.company;

                                      return (
                                        <tr
                                          key={company.contact_id}
                                          className="align-middle recent-contact-row"
                                          style={{ cursor: "pointer" }}
                                          onClick={() => {
                                            setSelectedContact(company);
                                            setShowContactModal(true);
                                          }}
                                        >
                                          <td className="ps-4 py-3">
                                            <div
                                              className="fw-medium text-dark text-truncate"
                                              style={{ maxWidth: 220 }}
                                            >
                                              {company.company_name ||
                                                "Unnamed Company"}
                                            </div>
                                          </td>

                                          <td className="py-3 text-muted">
                                            {company.contact_person_name || "—"}
                                          </td>

                                          <td
                                            className="py-3 text-muted text-truncate"
                                            style={{ maxWidth: 180 }}
                                          >
                                            {company.company_mail || "—"}
                                          </td>

                                          <td className="py-3 text-muted">
                                            {company.company_phone || "—"}
                                          </td>

                                          <td className="py-3">
                                            <Badge
                                              pill
                                              bg="primary-subtle"
                                              text="primary"
                                              className="px-3 py-2 fw-normal"
                                            >
                                              {company.leadsource_name ||
                                                "Unknown"}
                                            </Badge>
                                          </td>

                                          <td className="py-3 text-muted text-center">
                                            {company.created_at
                                              ? moment(
                                                  company.created_at,
                                                ).format("MMM DD, YYYY")
                                              : "—"}
                                          </td>

                                          <td className="py-3 text-center">
                                            <Button
                                              variant="outline-primary"
                                              size="sm"
                                              className="rounded-pill px-3"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(
                                                  `/crm/contact-list?name=${encodeURIComponent(
                                                    company.contact_person_name ||
                                                      "",
                                                  )}`,
                                                );
                                              }}
                                            >
                                              View
                                            </Button>
                                          </td>
                                        </tr>
                                      );
                                    })}

                                  {rangedRecentContacts.length === 0 && (
                                    <tr>
                                      <td
                                        colSpan={7}
                                        className="text-center text-muted py-5"
                                      >
                                        <div className="py-5">
                                          <FaUsers
                                            size={56}
                                            className="opacity-25 mb-4"
                                          />
                                          <h6 className="mb-2 fw-medium text-dark">
                                            No recent contacts found
                                          </h6>
                                          <p className="mb-0 small text-muted">
                                            Newly created contacts will appear
                                            here
                                          </p>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </Table>
                            </div>

                            {/* Footer */}
                            {rangedRecentContacts.length > 0 && (
                              <div className="px-4 py-3 bg-light border-top text-end small text-muted">
                                Showing{" "}
                                {Math.min(10, rangedRecentContacts.length)} of{" "}
                                {rangedRecentContacts.length} recent contacts
                              </div>
                            )}
                          </Card>

                          {/* Contact Detail Modal */}
                          <Modal
                            show={showContactModal}
                            onHide={() => setShowContactModal(false)}
                            centered
                            size="lg"
                          >
                            {/* ================= HEADER ================= */}
                            <Modal.Header closeButton className="border-bottom">
                              <div className="d-flex align-items-center gap-3">
                                <div className="bg-primary-subtle rounded-circle p-2">
                                  <FaBuilding
                                    className="text-primary"
                                    size={20}
                                  />
                                </div>
                                <div>
                                  <Modal.Title className="fw-semibold">
                                    Contact Details
                                  </Modal.Title>
                                  <small className="text-muted">
                                    {selectedContact?.contact_id || "—"}
                                  </small>
                                </div>
                              </div>
                            </Modal.Header>

                            {/* ================= BODY ================= */}
                            <Modal.Body className="p-4">
                              {selectedContact ? (
                                <>
                                  {/* ================= KPI STRIP ================= */}
                                  <Row className="g-3 mb-4">
                                    <Col md={4}>
                                      <div className="p-3 rounded-3 bg-primary-subtle text-center">
                                        <div className="small text-primary fw-medium">
                                          Company
                                        </div>
                                        <div className="fw-bold text-dark text-truncate">
                                          {selectedContact.company_name}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col md={4}>
                                      <div className="p-3 rounded-3 bg-success-subtle text-center">
                                        <div className="small text-success fw-medium">
                                          Lead Source
                                        </div>
                                        <div className="fw-bold text-dark">
                                          {selectedContact.leadsource_name ||
                                            "Unknown"}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col md={4}>
                                      <div className="p-3 rounded-3 bg-info-subtle text-center">
                                        <div className="small text-info fw-medium">
                                          Created On
                                        </div>
                                        <div className="fw-bold text-dark">
                                          {selectedContact.created_at
                                            ? moment(
                                                selectedContact.created_at,
                                              ).format("MMM DD, YYYY")
                                            : "—"}
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>

                                  {/* ================= DETAILS GRID ================= */}
                                  <Row className="g-4">
                                    {/* LEFT */}
                                    <Col md={6}>
                                      <div className="mb-3">
                                        <small className="text-muted">
                                          Contact Person
                                        </small>
                                        <div className="fw-medium">
                                          {selectedContact.contact_person_name}
                                        </div>
                                      </div>

                                      {selectedContact.contact_person_desigination && (
                                        <div className="mb-3">
                                          <small className="text-muted">
                                            Designation
                                          </small>
                                          <div className="fw-medium">
                                            {
                                              selectedContact.contact_person_desigination
                                            }
                                          </div>
                                        </div>
                                      )}

                                      <div className="mb-3">
                                        <small className="text-muted">
                                          Email
                                        </small>
                                        <div className="fw-medium">
                                          {selectedContact.company_mail || "—"}
                                        </div>
                                      </div>

                                      <div className="mb-3">
                                        <small className="text-muted">
                                          Phone
                                        </small>
                                        <div className="fw-medium">
                                          {selectedContact.company_phone || "—"}
                                        </div>
                                      </div>
                                    </Col>

                                    {/* RIGHT */}
                                    <Col md={6}>
                                      {selectedContact.website_url && (
                                        <div className="mb-3">
                                          <small className="text-muted">
                                            Website
                                          </small>
                                          <div className="fw-medium text-truncate">
                                            {selectedContact.website_url}
                                          </div>
                                        </div>
                                      )}

                                      
                                    </Col>
                                  </Row>

                                  {/* ================= ACTIVITY SUMMARY ================= */}
                                  <Row className="g-3 mt-4">
                                    

                                    <Col md={4}>
                                      <Badge
                                        bg="warning-subtle"
                                        text="warning"
                                        className="w-100 py-2"
                                      >
                                        Meta Page: {selectedContact.meta_page}
                                      </Badge>
                                    </Col>
                                  </Row>

                                  {/* ================= REMARKS ================= */}
                                  {selectedContact.contact_remark && (
                                    <div className="mt-4">
                                      <small className="text-muted">
                                        Remarks
                                      </small>
                                      <div className="p-3 rounded-3 bg-light">
                                        {selectedContact.contact_remark}
                                      </div>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="text-center text-muted py-5">
                                  No contact selected
                                </div>
                              )}
                            </Modal.Body>

                            {/* ================= FOOTER ================= */}
                            <Modal.Footer className="border-top">
                              <Button
                                variant="outline-secondary"
                                onClick={() => setShowContactModal(false)}
                              >
                                Close
                              </Button>

                              {/* <Button
                                variant="primary"
                                onClick={() =>
                                  navigate(
                                    `/crm/contact-list?contact_id=${encodeURIComponent(
                                      selectedContact?.contact_id || "",
                                    )}`,
                                  )
                                }
                              >
                                Open Full Contact
                              </Button> */}
                            </Modal.Footer>
                          </Modal>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Leads Section - Further Enhanced */}
                  <Card className="mb-5 shadow border-0 rounded-3">
                    <Card.Header className="bg-success text-white d-flex align-items-center rounded-top">
                      <FaChartBar className="me-2" size={20} /> Leads Overview{" "}
                      {renderTooltip(
                        "Detailed analysis of leads, distributions, and trends",
                      )}
                    </Card.Header>
                    <Card.Body className="p-4">
                      <Row className="g-3 mb-4">
                        {/* Total Leads */}
                        <Col xs={12} sm={4}>
                          <Card
                            className="h-100 border-0 rounded-4 dashboard-card"
                            body
                          >
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="kpi-icon bg-success-subtle text-success">
                                <FaDatabase size={22} />
                              </span>

                              <span className="badge bg-success-subtle text-success fs-11">
                                All Time
                              </span>
                            </div>

                            <div>
                              <small className="text-muted">Total Leads</small>
                              <h3 className="fw-bold text-dark mb-0">
                                {totalLeads.toLocaleString()}
                              </h3>
                            </div>
                          </Card>
                        </Col>

                        {/* Avg Lead Age */}
                        <Col xs={12} sm={4}>
                          <Card
                            className="h-100 border-0 rounded-4 dashboard-card"
                            body
                          >
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="kpi-icon bg-info-subtle text-info">
                                <FaHandHoldingUsd size={22} />
                              </span>

                              <span className="badge bg-info-subtle text-info fs-11">
                                Avg
                              </span>
                            </div>

                            <div>
                              <small className="text-muted">Avg Lead Age</small>
                              <h3 className="fw-bold text-dark mb-0">
                                {avgLeadAge.toFixed(1)}{" "}
                                <span className="fs-14 fw-normal text-muted">
                                  days
                                </span>
                              </h3>
                            </div>
                          </Card>
                        </Col>

                        {/* Latest Activity */}
                        <Col xs={12} sm={4}>
                          <Card
                            className="h-100 border-0 rounded-4 dashboard-card"
                            body
                          >
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="kpi-icon bg-warning-subtle text-warning">
                                <FaFilter size={22} />
                              </span>

                              <span className="badge bg-warning-subtle text-warning fs-11">
                                Recent
                              </span>
                            </div>

                            <div>
                              <small className="text-muted">
                                Latest Activity
                              </small>
                              <h3 className="fw-bold text-dark mb-0">
                                {recentLeads.length > 0
                                  ? moment(recentLeads[0].created_at).format(
                                      "MMM DD",
                                    )
                                  : "N/A"}
                              </h3>
                            </div>
                          </Card>
                        </Col>
                      </Row>

                      <Row className="g-4 mb-4">
                        <Col lg={6}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* Header */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-success-subtle rounded-circle p-2 me-3">
                                  <FaChartBar
                                    className="text-success"
                                    size={20}
                                  />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Status Distribution
                                  </h6>
                                  <small className="text-muted">
                                    How leads are spread across statuses
                                  </small>
                                </div>
                              </div>

                              <Tip text="Shows the number of contacts in each lead status">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* Body */}
                            <Card.Body className="p-4">
                              {statusData.length > 0 ? (
                                <>
                                  {/* 🔹 KPI STRIP */}
                                  <Row className="g-3 mb-4">
                                    <Col sm={4}>
                                      <div className="p-3 bg-primary-subtle rounded-3 text-center dashboard-card">
                                        <div className="small text-primary fw-medium">
                                          Total Statuses
                                        </div>
                                        <div className="fw-bold text-dark">
                                          {statusData.length}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col sm={4}>
                                      <div className="p-3 bg-success-subtle rounded-3 text-center dashboard-card">
                                        <div className="small text-success fw-medium">
                                          Top Status
                                        </div>
                                        <div className="fw-bold text-dark text-truncate">
                                          {highStatuses[0] || "—"}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col sm={4}>
                                      <div className="p-3 bg-warning-subtle rounded-3 text-center dashboard-card">
                                        <div className="small text-warning fw-medium">
                                          Least Used
                                        </div>
                                        <div className="fw-bold text-dark text-truncate">
                                          {lowStatuses[0] || "—"}
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>

                                  {/* 🔹 CHART */}
                                  <div
                                    className="position-relative"
                                    style={{
                                      height: 320,
                                      overflowX: "auto",
                                      overflowY: "hidden",
                                    }}
                                  >
                                    <ResponsiveContainer
                                      minWidth={statusData.length * 60}
                                    >
                                      <BarChart data={statusData}>
                                        <CartesianGrid
                                          strokeDasharray="3 3"
                                          stroke="#e5e7eb"
                                        />

                                        <XAxis
                                          dataKey="name"
                                          angle={-35}
                                          textAnchor="end"
                                          height={70}
                                          interval={0}
                                          tick={{ fontSize: 12 }}
                                        />

                                        <YAxis tick={{ fontSize: 12 }} />

                                        <RechartsTooltip
                                          content={customChartTooltip}
                                          cursor={{
                                            fill: "rgba(34,197,94,0.08)",
                                          }}
                                        />

                                        <Bar
                                          dataKey="value"
                                          fill="#22c55e"
                                          radius={[6, 6, 0, 0]}
                                          barSize={32}
                                        />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </div>

                                  {/* 🔹 INSIGHT */}
                                  <div className="mt-4 text-center small text-muted">
                                    <strong>{highStatuses[0]}</strong> is the
                                    most used status, while{" "}
                                    <strong>{lowStatuses[0]}</strong> appears
                                    least frequently
                                  </div>
                                </>
                              ) : (
                                /* Empty State */
                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                                  <FaChartBar
                                    size={56}
                                    className="opacity-25 mb-4"
                                  />
                                  <h6 className="mb-2 fw-medium text-dark">
                                    No status data available
                                  </h6>
                                  <p className="mb-0 small">
                                    Status distribution will appear once leads
                                    are created
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col lg={6}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* Header */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-success-subtle rounded-circle p-2 me-3">
                                  <FaChartPie
                                    className="text-success"
                                    size={20}
                                  />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Source Distribution
                                  </h6>
                                  <small className="text-muted">
                                    Where leads are coming from
                                  </small>
                                </div>
                              </div>

                              <Tip text="Shows how leads are distributed across different sources">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* Body */}
                            <Card.Body className="p-4">
                              {leadSourceLeadData.length > 0 ? (
                                <>
                                  {/* 🔹 KPI STRIP */}
                                  <Row className="g-3 mb-4">
                                    <Col sm={4}>
                                      <div className="p-3 bg-primary-subtle rounded-3 text-center dashboard-card">
                                        <div className="small text-primary fw-medium">
                                          Total Sources
                                        </div>
                                        <div className="fw-bold text-dark">
                                          {leadSourceLeadData.length}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col sm={4}>
                                      <div className="p-3 bg-success-subtle rounded-3 text-center dashboard-card">
                                        <div className="small text-success fw-medium">
                                          Top Source
                                        </div>
                                        <div className="fw-bold text-dark text-truncate">
                                          {leadSourceLeadData[0]?.name || "—"}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col sm={4}>
                                      <div className="p-3 bg-warning-subtle rounded-3 text-center dashboard-card">
                                        <div className="small text-warning fw-medium">
                                          Least Used
                                        </div>
                                        <div className="fw-bold text-dark text-truncate">
                                          {leadSourceLeadData.at(-1)?.name ||
                                            "—"}
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>

                                  {/* 🔹 CHART */}
                                  <div
                                    className="position-relative"
                                    style={{ height: 320 }}
                                  >
                                    <ResponsiveContainer>
                                      <PieChart>
                                        <Pie
                                          data={leadSourceLeadData}
                                          dataKey="value"
                                          nameKey="name"
                                          cx="50%"
                                          cy="50%"
                                          innerRadius={80}
                                          outerRadius={120}
                                          paddingAngle={4}
                                          labelLine={false}
                                          isAnimationActive
                                          animationBegin={150}
                                          animationDuration={800}
                                          animationEasing="ease-out"
                                        >
                                          {leadSourceLeadData.map(
                                            (_, index) => (
                                              <Cell
                                                key={index}
                                                fill={
                                                  COLORS[index % COLORS.length]
                                                }
                                                stroke="#fff"
                                                strokeWidth={2}
                                              />
                                            ),
                                          )}
                                        </Pie>

                                        <RechartsTooltip
                                          content={customChartTooltip}
                                          cursor={{
                                            fill: "rgba(34,197,94,0.08)",
                                          }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>

                                    {/* Center KPI */}
                                    <div className="position-absolute top-50 start-50 translate-middle text-center">
                                      <div className="fw-bold text-dark">
                                        {leadSourceLeadData.reduce(
                                          (sum, s) => sum + s.value,
                                          0,
                                        )}
                                      </div>
                                      <div className="small text-muted">
                                        Total Leads
                                      </div>
                                    </div>
                                  </div>

                                  {/* 🔹 RANKED LIST */}
                                  <div className="mt-4">
                                    {leadSourceLeadData
                                      .slice(0, 5)
                                      .map((d, index) => (
                                        <div
                                          key={d.name}
                                          className="d-flex align-items-center justify-content-between mb-2 small ranked-row"
                                        >
                                          <div className="d-flex align-items-center gap-2">
                                            <span
                                              className="rounded-circle"
                                              style={{
                                                width: 10,
                                                height: 10,
                                                backgroundColor:
                                                  COLORS[index % COLORS.length],
                                              }}
                                            />
                                            <span className="fw-medium text-dark">
                                              {d.name}
                                            </span>
                                          </div>
                                          <span className="text-muted">
                                            {d.value as number}
                                          </span>
                                        </div>
                                      ))}
                                  </div>

                                  {/* 🔹 INSIGHT */}
                                  <div className="text-center mt-4 small text-muted">
                                    <strong>
                                      {leadSourceLeadData[0]?.name}
                                    </strong>{" "}
                                    contributes{" "}
                                    <span className="fw-semibold text-success">
                                      {(
                                        (leadSourceLeadData[0]?.value /
                                          leadSourceLeadData.reduce(
                                            (sum, s) => sum + s.value,
                                            0,
                                          )) *
                                        100
                                      ).toFixed(1)}
                                      %
                                    </span>{" "}
                                    of all leads
                                  </div>
                                </>
                              ) : (
                                /* Empty State */
                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                                  <FaChartPie
                                    size={56}
                                    className="opacity-25 mb-4"
                                  />
                                  <h6 className="mb-2 fw-medium text-dark">
                                    No source data available
                                  </h6>
                                  <p className="mb-0 small">
                                    Lead sources will appear once leads are
                                    created
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      <Row className="g-4 mb-4">
                        <Col lg={12}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* Header */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-success-subtle rounded-circle p-2 me-3">
                                  <FaChartPie
                                    className="text-success"
                                    size={18}
                                  />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Stage Distribution
                                  </h6>
                                  <small className="text-muted">
                                    Lead progress by stage
                                  </small>
                                </div>
                              </div>

                              <Tip text="Shows how leads are distributed across different stages">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* Body */}
                            <Card.Body className="p-4">
                              {leadStageData.length > 0 ? (
                                <>
                                  {/* 🔹 KPI STRIP */}
                                  <Row className="g-2 mb-4">
                                    <Col xs={4}>
                                      <div className="p-2 rounded-3 bg-primary-subtle text-center dashboard-card">
                                        <div className="small text-primary fw-medium">
                                          Total Stages
                                        </div>
                                        <div className="fw-bold text-dark">
                                          {leadStageData.length}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col xs={4}>
                                      <div className="p-2 rounded-3 bg-success-subtle text-center dashboard-card">
                                        <div className="small text-success fw-medium">
                                          Top Stage
                                        </div>
                                        <div className="fw-bold text-dark text-truncate">
                                          {leadStageData[0]?.name || "—"}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col xs={4}>
                                      <div className="p-2 rounded-3 bg-warning-subtle text-center dashboard-card">
                                        <div className="small text-warning fw-medium">
                                          Least
                                        </div>
                                        <div className="fw-bold text-dark text-truncate">
                                          {leadStageData.at(-1)?.name || "—"}
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>

                                  {/* 🔹 CHART */}
                                  <div
                                    className="position-relative"
                                    style={{ height: 240 }}
                                  >
                                    <ResponsiveContainer>
                                      <PieChart>
                                        <Pie
                                          data={leadStageData}
                                          dataKey="value"
                                          nameKey="name"
                                          cx="50%"
                                          cy="50%"
                                          innerRadius={55}
                                          outerRadius={90}
                                          paddingAngle={4}
                                          labelLine={false}
                                          isAnimationActive
                                          animationBegin={150}
                                          animationDuration={800}
                                          animationEasing="ease-out"
                                        >
                                          {leadStageData.map((_, index) => (
                                            <Cell
                                              key={index}
                                              fill={
                                                COLORS[index % COLORS.length]
                                              }
                                              stroke="#fff"
                                              strokeWidth={2}
                                            />
                                          ))}
                                        </Pie>

                                        <RechartsTooltip
                                          content={customChartTooltip}
                                          cursor={{
                                            fill: "rgba(34,197,94,0.08)",
                                          }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>

                                    {/* Center KPI */}
                                    <div className="position-absolute top-50 start-50 translate-middle text-center">
                                      <div className="fw-bold text-dark">
                                        {leadStageData.reduce(
                                          (sum, s) =>
                                            sum +
                                            (s as { value: number }).value,
                                          0,
                                        )}
                                      </div>
                                      <div className="small text-muted">
                                        Total Leads
                                      </div>
                                    </div>
                                  </div>

                                  {/* 🔹 RANKED LIST */}
                                  <div className="mt-4">
                                    {leadStageData
                                      .slice(0, 4)
                                      .map((d, index) => (
                                        <div
                                          key={d.name}
                                          className="d-flex align-items-center justify-content-between mb-2 small ranked-row"
                                        >
                                          <div className="d-flex align-items-center gap-2">
                                            <span
                                              className="rounded-circle"
                                              style={{
                                                width: 10,
                                                height: 10,
                                                backgroundColor:
                                                  COLORS[index % COLORS.length],
                                              }}
                                            />
                                            <span className="fw-medium text-dark">
                                              {d.name}
                                            </span>
                                          </div>
                                          <span className="text-muted">
                                            {d.value as number}
                                          </span>
                                        </div>
                                      ))}
                                  </div>

                                  {/* 🔹 INSIGHT */}
                                  <div className="text-center mt-4 small text-muted">
                                    <strong>{leadStageData[0]?.name}</strong>{" "}
                                    stage contains{" "}
                                    <span className="fw-semibold text-success">
                                      {(
                                        ((leadStageData[0]?.value as number) /
                                          leadStageData.reduce(
                                            (sum, s) =>
                                              sum + (s.value as number),
                                            0,
                                          )) *
                                        100
                                      ).toFixed(1)}
                                      %
                                    </span>{" "}
                                    of all leads
                                  </div>
                                </>
                              ) : (
                                /* Empty State */
                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                                  <FaChartPie
                                    size={48}
                                    className="opacity-25 mb-3"
                                  />
                                  <h6 className="mb-1 fw-medium text-dark">
                                    No stage data available
                                  </h6>
                                  <p className="mb-0 small">
                                    Lead stages will appear once leads are added
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col lg={6}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* Header */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-success-subtle rounded-circle p-2 me-3">
                                  <FaChartBar
                                    className="text-success"
                                    size={18}
                                  />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Type Distribution
                                  </h6>
                                  <small className="text-muted">
                                    Leads grouped by type
                                  </small>
                                </div>
                              </div>

                              <Tip text="Shows how leads are distributed across different lead types">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* Body */}
                            <Card.Body className="p-4">
                              {leadTypeData.length > 0 ? (
                                <>
                                  {/* 🔹 KPI STRIP */}
                                  <Row className="g-3 mb-4">
                                    <Col sm={4}>
                                      <div className="p-3 rounded-3 bg-primary-subtle text-center dashboard-card">
                                        <div className="small text-primary fw-medium">
                                          Total Types
                                        </div>
                                        <div className="fw-bold text-dark">
                                          {leadTypeData.length}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col sm={4}>
                                      <div className="p-3 rounded-3 bg-success-subtle text-center dashboard-card">
                                        <div className="small text-success fw-medium">
                                          Top Type
                                        </div>
                                        <div className="fw-bold text-dark text-truncate">
                                          {leadTypeData[0]?.name || "—"}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col sm={4}>
                                      <div className="p-3 rounded-3 bg-warning-subtle text-center dashboard-card">
                                        <div className="small text-warning fw-medium">
                                          Least Used
                                        </div>
                                        <div className="fw-bold text-dark text-truncate">
                                          {leadTypeData.at(-1)?.name || "—"}
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>

                                  {/* 🔹 CHART */}
                                  <div
                                    style={{
                                      position: "relative",
                                      height: 260,
                                      overflowX: "auto",
                                      overflowY: "hidden",
                                    }}
                                  >
                                    <ResponsiveContainer
                                      minWidth={Math.max(
                                        leadTypeData.length * 70,
                                        300,
                                      )}
                                    >
                                      <BarChart
                                        data={leadTypeData}
                                        layout="vertical"
                                        margin={{ left: 20, right: 20 }}
                                      >
                                        <CartesianGrid
                                          strokeDasharray="3 3"
                                          horizontal={false}
                                        />

                                        <XAxis type="number" hide />

                                        <YAxis
                                          dataKey="name"
                                          type="category"
                                          width={120}
                                          tick={{ fontSize: 12 }}
                                        />

                                        <RechartsTooltip
                                          content={customChartTooltip}
                                          cursor={{
                                            fill: "rgba(34,197,94,0.08)",
                                          }}
                                        />

                                        <Bar
                                          dataKey="value"
                                          radius={[0, 6, 6, 0]}
                                          fill="#22c55e"
                                          barSize={18}
                                          isAnimationActive
                                          animationBegin={100}
                                          animationDuration={700}
                                        />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </div>

                                  {/* 🔹 RANKED LIST */}
                                  <div className="mt-4">
                                    {leadTypeData
                                      .slice(0, 5)
                                      .map((d, index) => (
                                        <div
                                          key={d.name}
                                          className="d-flex align-items-center justify-content-between mb-2 small ranked-row"
                                        >
                                          <div className="d-flex align-items-center gap-2">
                                            <span
                                              className="rounded-circle"
                                              style={{
                                                width: 10,
                                                height: 10,
                                                backgroundColor: "#22c55e",
                                                opacity: 1 - index * 0.15,
                                              }}
                                            />
                                            <span className="fw-medium text-dark">
                                              {d.name}
                                            </span>
                                          </div>
                                          <span className="text-muted">
                                            {d.value as number}
                                          </span>
                                        </div>
                                      ))}
                                  </div>

                                  {/* 🔹 INSIGHT */}
                                  <div className="text-center mt-4 small text-muted">
                                    <strong>{leadTypeData[0]?.name}</strong> is
                                    the most used type, contributing{" "}
                                    <span className="fw-semibold text-success">
                                      {(
                                        ((leadTypeData[0]?.value as number) /
                                          leadTypeData.reduce(
                                            (sum, s) =>
                                              sum + (s.value as number),
                                            0,
                                          )) *
                                        100
                                      ).toFixed(1)}
                                      %
                                    </span>{" "}
                                    of all leads
                                  </div>
                                </>
                              ) : (
                                /* Empty State */
                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                                  <FaChartBar
                                    size={48}
                                    className="opacity-25 mb-3"
                                  />
                                  <h6 className="mb-1 fw-medium text-dark">
                                    No type data available
                                  </h6>
                                  <p className="mb-0 small">
                                    Lead types will appear once leads are added
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col lg={6}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* Header */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-warning-subtle rounded-circle p-2 me-3">
                                  <FaChartBar
                                    className="text-warning"
                                    size={18}
                                  />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Industry Distribution
                                  </h6>
                                  <small className="text-muted">
                                    Leads grouped by industry
                                  </small>
                                </div>
                              </div>

                              <Tip text="Shows how leads are distributed across different industries">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* Body */}
                            <Card.Body className="p-4">
                              {leadIndustryData.length > 0 ? (
                                <>
                                  {/* 🔹 KPI STRIP */}
                                  <Row className="g-3 mb-4">
                                    <Col sm={4}>
                                      <div className="p-3 rounded-3 bg-primary-subtle text-center dashboard-card">
                                        <div className="small text-primary fw-medium">
                                          Total Industries
                                        </div>
                                        <div className="fw-bold text-dark">
                                          {leadIndustryData.length}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col sm={4}>
                                      <div className="p-3 rounded-3 bg-warning-subtle text-center dashboard-card">
                                        <div className="small text-warning fw-medium">
                                          Top Industry
                                        </div>
                                        <div className="fw-bold text-dark text-truncate">
                                          {leadIndustryData[0]?.name || "—"}
                                        </div>
                                      </div>
                                    </Col>

                                    <Col sm={4}>
                                      <div className="p-3 rounded-3 bg-info-subtle text-center dashboard-card">
                                        <div className="small text-info fw-medium">
                                          Least Used
                                        </div>
                                        <div className="fw-bold text-dark text-truncate">
                                          {leadIndustryData.at(-1)?.name || "—"}
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>

                                  {/* 🔹 CHART */}
                                  <div
                                    style={{
                                      position: "relative",
                                      height: 260,
                                      overflowX: "auto",
                                      overflowY: "hidden",
                                    }}
                                  >
                                    <ResponsiveContainer
                                      minWidth={Math.max(
                                        leadIndustryData.length * 70,
                                        320,
                                      )}
                                    >
                                      <BarChart
                                        data={leadIndustryData}
                                        margin={{
                                          left: 10,
                                          right: 20,
                                          bottom: 30,
                                        }}
                                      >
                                        <CartesianGrid
                                          strokeDasharray="3 3"
                                          vertical={false}
                                        />

                                        <XAxis
                                          dataKey="name"
                                          angle={-35}
                                          textAnchor="end"
                                          height={60}
                                          tick={{ fontSize: 11 }}
                                          interval={0}
                                        />

                                        <YAxis hide />

                                        <RechartsTooltip
                                          content={customChartTooltip}
                                          cursor={{
                                            fill: "rgba(234,179,8,0.1)",
                                          }}
                                        />

                                        <Bar
                                          dataKey="value"
                                          fill="#f59e0b"
                                          radius={[6, 6, 0, 0]}
                                          barSize={22}
                                          isAnimationActive
                                          animationBegin={100}
                                          animationDuration={700}
                                        />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </div>

                                  {/* 🔹 RANKED LIST */}
                                  <div className="mt-4">
                                    {leadIndustryData
                                      .slice(0, 5)
                                      .map((d, index) => (
                                        <div
                                          key={d.name}
                                          className="d-flex align-items-center justify-content-between mb-2 small ranked-row"
                                        >
                                          <div className="d-flex align-items-center gap-2">
                                            <span
                                              className="rounded-circle"
                                              style={{
                                                width: 10,
                                                height: 10,
                                                backgroundColor: "#f59e0b",
                                                opacity: 1 - index * 0.15,
                                              }}
                                            />
                                            <span className="fw-medium text-dark">
                                              {d.name}
                                            </span>
                                          </div>
                                          <span className="text-muted">
                                            {d.value}
                                          </span>
                                        </div>
                                      ))}
                                  </div>

                                  {/* 🔹 INSIGHT */}
                                  <div className="text-center mt-4 small text-muted">
                                    <strong>{leadIndustryData[0]?.name}</strong>{" "}
                                    contributes{" "}
                                    <span className="fw-semibold text-warning">
                                      {(
                                        (leadIndustryData[0]?.value /
                                          leadIndustryData.reduce(
                                            (sum, i) => sum + i.value,
                                            0,
                                          )) *
                                        100
                                      ).toFixed(1)}
                                      %
                                    </span>{" "}
                                    of all leads
                                  </div>
                                </>
                              ) : (
                                /* Empty State */
                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                                  <FaChartBar
                                    size={48}
                                    className="opacity-25 mb-3"
                                  />
                                  <h6 className="mb-1 fw-medium text-dark">
                                    No industry data available
                                  </h6>
                                  <p className="mb-0 small">
                                    Industry information will appear once leads
                                    are categorized
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                      <Row className="g-4 mb-4">
                        {/* ================= PRODUCT INTEREST ================= */}
                        <Col md={4}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* Header */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-success-subtle rounded-circle p-2 me-3">
                                  <FaChartPie
                                    className="text-success"
                                    size={18}
                                  />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Product Interest
                                  </h6>
                                  <small className="text-muted">
                                    What customers are interested in
                                  </small>
                                </div>
                              </div>

                              <Tip text="Distribution of leads based on product interest">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* Body */}
                            <Card.Body className="p-4">
                              {productData.length > 0 ? (
                                <>
                                  {/* KPI */}
                                  <div className="mb-3 text-center">
                                    <div className="small text-success fw-medium">
                                      Total Products
                                    </div>
                                    <div className="fw-bold  text-dark">
                                      {productData.length}
                                    </div>
                                  </div>

                                  {/* Chart */}
                                  <div style={{ height: 220 }}>
                                    <ResponsiveContainer>
                                      <PieChart>
                                        <Pie
                                          data={productData}
                                          dataKey="value"
                                          nameKey="name"
                                          cx="50%"
                                          cy="50%"
                                          innerRadius={45}
                                          outerRadius={80}
                                          paddingAngle={3}
                                          labelLine={false}
                                        >
                                          {productData.map((_, index) => (
                                            <Cell
                                              key={index}
                                              fill={
                                                COLORS[index % COLORS.length]
                                              }
                                              stroke="#fff"
                                              strokeWidth={2}
                                            />
                                          ))}
                                        </Pie>

                                        <RechartsTooltip
                                          content={customChartTooltip}
                                          cursor={{
                                            fill: "rgba(34,197,94,0.08)",
                                          }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>
                                  </div>

                                  {/* Ranked List */}
                                  <div className="mt-4">
                                    {productData.slice(0, 3).map((d, index) => (
                                      <div
                                        key={d.name}
                                        className="d-flex justify-content-between align-items-center small mb-2 ranked-row"
                                      >
                                        <span className="fw-medium text-dark">
                                          {d.name}
                                        </span>
                                        <span className="text-muted">
                                          {" "}
                                          {d.value as number}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              ) : (
                                <div className="text-center text-muted py-5">
                                  No product interest data
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>

                        {/* ================= PRODUCT TYPE ================= */}
                        <Col md={4}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* Header */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-info-subtle rounded-circle p-2 me-3">
                                  <FaChartPie className="text-info" size={18} />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Product Type
                                  </h6>
                                  <small className="text-muted">
                                    Category-wise interest
                                  </small>
                                </div>
                              </div>

                              <Tip text="Breakdown of leads by product type">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* Body */}
                            <Card.Body className="p-4">
                              {productTypeData.length > 0 ? (
                                <>
                                  {/* KPI */}
                                  <div className="mb-3 text-center">
                                    <div className="small text-info fw-medium">
                                      Types Used
                                    </div>
                                    <div className="fw-bold text-dark">
                                      {productTypeData.length}
                                    </div>
                                  </div>

                                  {/* Chart */}
                                  <div style={{ height: 220 }}>
                                    <ResponsiveContainer>
                                      <PieChart>
                                        <Pie
                                          data={productTypeData}
                                          dataKey="value"
                                          nameKey="name"
                                          cx="50%"
                                          cy="50%"
                                          innerRadius={45}
                                          outerRadius={80}
                                          paddingAngle={3}
                                          labelLine={false}
                                        >
                                          {productTypeData.map((_, index) => (
                                            <Cell
                                              key={index}
                                              fill={
                                                COLORS[
                                                  (index + 2) % COLORS.length
                                                ]
                                              }
                                              stroke="#fff"
                                              strokeWidth={2}
                                            />
                                          ))}
                                        </Pie>

                                        <RechartsTooltip
                                          content={customChartTooltip}
                                          cursor={{
                                            fill: "rgba(59,130,246,0.08)",
                                          }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>
                                  </div>

                                  {/* Ranked */}
                                  <div className="mt-4">
                                    {productTypeData.slice(0, 3).map((d) => (
                                      <div
                                        key={d.name}
                                        className="d-flex justify-content-between small mb-2 ranked-row"
                                      >
                                        <span className="fw-medium text-dark">
                                          {d.name}
                                        </span>
                                        <span className="text-muted">
                                          {" "}
                                          {d.value as number}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              ) : (
                                <div className="text-center text-muted py-5">
                                  No product type data
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>

                        {/* ================= CUSTOMER TYPE ================= */}
                        <Col md={4}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* Header */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-warning-subtle rounded-circle p-2 me-3">
                                  <FaChartBar
                                    className="text-warning"
                                    size={18}
                                  />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Customer Type
                                  </h6>
                                  <small className="text-muted">
                                    Lead classification
                                  </small>
                                </div>
                              </div>

                              <Tip text="Distribution of leads by customer type">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* Body */}
                            <Card.Body className="p-4">
                              {customerTypeData.length > 0 ? (
                                <>
                                  {/* KPI */}
                                  <div className="mb-3 text-center">
                                    <div className="small text-warning fw-medium">
                                      Customer Segments
                                    </div>
                                    <div className="fw-bold text-dark">
                                      {customerTypeData.length}
                                    </div>
                                  </div>

                                  {/* Bar Chart */}
                                  <div style={{ height: 220 }}>
                                    <ResponsiveContainer>
                                      <BarChart data={customerTypeData}>
                                        <CartesianGrid
                                          strokeDasharray="3 3"
                                          vertical={false}
                                        />
                                        <XAxis
                                          dataKey="name"
                                          tick={{ fontSize: 11 }}
                                        />
                                        <YAxis hide />
                                        <RechartsTooltip
                                          content={customChartTooltip}
                                        />
                                        <Bar
                                          dataKey="value"
                                          fill="#f59e0b"
                                          radius={[6, 6, 0, 0]}
                                          barSize={26}
                                        />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </div>

                                  {/* Ranked */}
                                  <div className="mt-4">
                                    {customerTypeData.slice(0, 3).map((d) => (
                                      <div
                                        key={d.name}
                                        className="d-flex justify-content-between small mb-2 ranked-row"
                                      >
                                        <span className="fw-medium text-dark">
                                          {d.name}
                                        </span>
                                        <span className="text-muted">
                                          {" "}
                                          {d.value as number}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              ) : (
                                <div className="text-center text-muted py-5">
                                  No customer type data
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      <Col lg={12}>
                        <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                          {/* Header */}
                          <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <div className="bg-success-subtle rounded-circle p-2 me-3">
                                <FaList className="text-success" size={20} />
                              </div>
                              <div>
                                <h6 className="mb-0 fw-semibold text-dark">
                                  Recent Leads
                                </h6>
                                <small className="text-muted">
                                  Latest 10 leads created
                                </small>
                              </div>
                            </div>

                            <Tip text="Most recently created leads. Click a row to view details">
                              <i className="fa fa-info-circle text-muted fs-14" />
                            </Tip>
                          </Card.Header>

                          {/* Table */}
                          <div
                            className="table-responsive"
                            style={{ maxHeight: 420 }}
                          >
                            <Table
                              hover
                              className="mb-0 table-borderless recent-leads-table"
                            >
                              <thead
                                className="bg-light-subtle text-muted small"
                                style={{
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 1,
                                }}
                              >
                                <tr>
                                  <th className="ps-4">#</th>
                                  <th>Lead ID</th>
                                  <th>Company</th>
                                  <th>Status</th>
                                  <th>Source</th>
                                  <th>Stage</th>
                                  <th className="text-center">Created</th>
                                  <th />
                                </tr>
                              </thead>

                              <tbody>
                                {recentLeads.slice(0, 10).map((lead, index) => (
                                  <tr
                                    key={lead.lead_id}
                                    className="align-middle lead-row"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      setSelectedLead(lead);
                                      setIsLeadModalOpen(true);
                                    }}
                                  >
                                    <td className="ps-4 fw-medium">
                                      {index + 1}
                                    </td>

                                    <td
                                      className="text-truncate text-muted"
                                      style={{ maxWidth: 120 }}
                                    >
                                      {lead.lead_id}
                                    </td>

                                    <td
                                      className="fw-medium text-dark text-truncate"
                                      style={{ maxWidth: 180 }}
                                    >
                                      {lead.contact?.company_name || "N/A"}
                                    </td>

                                    <td>
                                      <span
                                        className={`badge fs-11 ${
                                          lead.lead_status === "Contacted"
                                            ? "bg-success-subtle text-success"
                                            : lead.lead_status === "Lost"
                                              ? "bg-danger-subtle text-danger"
                                              : "bg-warning-subtle text-warning"
                                        }`}
                                      >
                                        {lead.lead_status}
                                      </span>
                                    </td>

                                    <td
                                      className="text-truncate"
                                      style={{ maxWidth: 140 }}
                                    >
                                      {lead.leadsource?.LeadSource_name ||
                                        "Unknown"}
                                    </td>

                                    <td
                                      className="text-truncate"
                                      style={{ maxWidth: 140 }}
                                    >
                                      {lead.leadstage?.LeadStage_name || "N/A"}
                                    </td>

                                    <td className="text-center text-muted fs-12">
                                      {lead.created_at
                                        ? moment(lead.created_at).format(
                                            "MMM DD, YYYY",
                                          )
                                        : "—"}
                                    </td>

                                    <td className="text-end pe-3">
                                      <i className="ti ti-chevron-right text-muted" />
                                    </td>
                                  </tr>
                                ))}

                                {recentLeads.length === 0 && (
                                  <tr>
                                    <td
                                      colSpan={8}
                                      className="text-center py-5 text-muted"
                                    >
                                      <FaUsers
                                        size={48}
                                        className="opacity-25 mb-3"
                                      />
                                      <h6 className="mb-1 text-dark">
                                        No recent leads
                                      </h6>
                                      <p className="small mb-0">
                                        Newly created leads will appear here
                                      </p>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </div>

                          {/* Footer */}
                          {recentLeads.length > 0 && (
                            <div className="px-4 py-3 bg-light border-top text-end small text-muted">
                              Showing {Math.min(10, recentLeads.length)} of{" "}
                              {recentLeads.length} leads
                            </div>
                          )}
                        </Card>
                      </Col>
                      <Modal
                        show={isLeadModalOpen}
                        onHide={() => setIsLeadModalOpen(false)}
                        centered
                        size="lg"
                      >
                        {/* ================= HEADER ================= */}
                        <Modal.Header closeButton className="border-bottom">
                          <div className="d-flex align-items-center gap-3">
                            <div className="bg-primary-subtle rounded-circle p-2">
                              <FaUsers className="text-primary" size={20} />
                            </div>
                            <div>
                              <Modal.Title className="fw-semibold">
                                Lead Details
                              </Modal.Title>
                              <small className="text-muted">
                                {selectedLead?.lead_id || "—"}
                              </small>
                            </div>
                          </div>
                        </Modal.Header>

                        {/* ================= BODY ================= */}
                        <Modal.Body className="p-4">
                          {selectedLead ? (
                            <>
                              {/* ================= KPI STRIP ================= */}
                              <Row className="g-3 mb-4">
                                <Col md={4}>
                                  <div className="p-3 rounded-3 bg-success-subtle text-center">
                                    <div className="small text-success fw-medium">
                                      Lead Value
                                    </div>
                                    <div className="fw-bold text-dark">
                                      {selectedLead.Currency || "₹"}{" "}
                                      {Number(
                                        selectedLead.lead_value || 0,
                                      ).toLocaleString()}
                                    </div>
                                  </div>
                                </Col>

                                <Col md={4}>
                                  <div className="p-3 rounded-3 bg-warning-subtle text-center">
                                    <div className="small text-warning fw-medium">
                                      Status
                                    </div>
                                    <div className="fw-bold text-dark">
                                      {selectedLead.lead_status}
                                    </div>
                                  </div>
                                </Col>

                                <Col md={4}>
                                  <div className="p-3 rounded-3 bg-info-subtle text-center">
                                    <div className="small text-info fw-medium">
                                      Stage
                                    </div>
                                    <div className="fw-bold text-dark">
                                      {selectedLead.leadstage?.LeadStage_name ||
                                        "—"}
                                    </div>
                                  </div>
                                </Col>
                              </Row>

                              {/* ================= DETAILS GRID ================= */}
                              <Row className="g-4">
                                {/* LEFT */}
                                <Col md={6}>
                                  <div className="mb-3">
                                    <small className="text-muted">
                                      Company
                                    </small>
                                    <div className="fw-medium">
                                      {selectedLead.contact?.company_name ||
                                        "—"}
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <small className="text-muted">
                                      Contact Person
                                    </small>
                                    <div className="fw-medium">
                                      {selectedLead.contact
                                        ?.contact_person_name || "—"}
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <small className="text-muted">Email</small>
                                    <div className="fw-medium">
                                      {selectedLead.contact?.company_mail ||
                                        "—"}
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <small className="text-muted">Phone</small>
                                    <div className="fw-medium">
                                      {selectedLead.contact?.company_phone ||
                                        "—"}
                                    </div>
                                  </div>
                                </Col>

                                {/* RIGHT */}
                                <Col md={6}>
                                  <div className="mb-3">
                                    <small className="text-muted">
                                      Lead Source
                                    </small>
                                    <div className="fw-medium">
                                      {selectedLead.leadsource
                                        ?.LeadSource_name || "—"}
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <small className="text-muted">
                                      Lead Type
                                    </small>
                                    <div className="fw-medium">
                                      {selectedLead.leadtype?.leadtype_name ||
                                        "—"}
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <small className="text-muted">
                                      Industry
                                    </small>
                                    <div className="fw-medium">
                                      {selectedLead.leadindustry
                                        ?.LeadIndustry_name || "—"}
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <small className="text-muted">
                                      Created On
                                    </small>
                                    <div className="fw-medium">
                                      {moment(selectedLead.created_at).format(
                                        "MMM DD, YYYY",
                                      )}
                                    </div>
                                  </div>
                                </Col>
                              </Row>

                              {/* ================= PRODUCT & PRICING ================= */}
                              {selectedLead.products && (
                                <div className="mt-4 p-3 rounded-3 bg-light">
                                  <div className="fw-semibold mb-2">
                                    Product & Pricing
                                  </div>

                                  <Row className="small">
                                    <Col md={4}>
                                      <div className="text-muted">Product</div>
                                      <div className="fw-medium">
                                        {selectedLead.products.product_name}
                                      </div>
                                    </Col>

                                    <Col md={4}>
                                      <div className="text-muted">Logins</div>
                                      <div className="fw-medium">
                                        {selectedLead.products.no_logins}
                                      </div>
                                    </Col>

                                    <Col md={4}>
                                      <div className="text-muted">
                                        Rate / Login
                                      </div>
                                      <div className="fw-medium">
                                        ₹{selectedLead.products.rate_per_login}
                                      </div>
                                    </Col>
                                  </Row>

                                  <hr className="my-2" />

                                  <Row className="small">
                                    <Col md={3}>
                                      <div className="text-muted">Amount</div>
                                      <div className="fw-medium">
                                        ₹{selectedLead.amount}
                                      </div>
                                    </Col>

                                    <Col md={3}>
                                      <div className="text-muted">Discount</div>
                                      <div className="fw-medium text-danger">
                                        {selectedLead.proposal_discount_percentage ||
                                          0}
                                        %
                                      </div>
                                    </Col>

                                    <Col md={3}>
                                      <div className="text-muted">Tax</div>
                                      <div className="fw-medium">
                                        {selectedLead.proposal_tax_percentage ||
                                          0}
                                        %
                                      </div>
                                    </Col>

                                    <Col md={3}>
                                      <div className="text-muted">Total</div>
                                      <div className="fw-bold">
                                        ₹{selectedLead.total_amount}
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              )}

                              {/* ================= TAX BREAKDOWN ================= */}
                              {(selectedLead.cgst ||
                                selectedLead.sgst ||
                                selectedLead.igst) && (
                                <div className="mt-3 p-3 rounded-3 bg-white border">
                                  <div className="fw-semibold mb-2">
                                    Tax Breakdown
                                  </div>
                                  <Row className="small">
                                    <Col md={4}>
                                      CGST ({selectedLead.cgst_percentage}%):{" "}
                                      <strong>{selectedLead.cgst}</strong>
                                    </Col>
                                    <Col md={4}>
                                      SGST ({selectedLead.sgst_percentage}%):{" "}
                                      <strong>{selectedLead.sgst}</strong>
                                    </Col>
                                    <Col md={4}>
                                      IGST ({selectedLead.igst_percentage}%):{" "}
                                      <strong>{selectedLead.igst}</strong>
                                    </Col>
                                  </Row>
                                </div>
                              )}

                              {/* ================= OWNER & FLAGS ================= */}
                              <Row className="g-3 mt-3">
                                <Col md={6}>
                                  <small className="text-muted">
                                    Assigned To
                                  </small>
                                  <div className="fw-medium">
                                    {selectedLead.users?.name || "—"}
                                  </div>
                                </Col>

                                <Col md={6}>
                                  <small className="text-muted">
                                    WhatsApp Group
                                  </small>
                                  <div className="fw-medium">
                                    {selectedLead.whatsapp_group || "—"}
                                  </div>
                                </Col>
                              </Row>

                              {/* ================= DESCRIPTION ================= */}
                              {selectedLead.lead_description && (
                                <div className="mt-4">
                                  <small className="text-muted">
                                    Description
                                  </small>
                                  <div className="p-3 rounded-3 bg-light">
                                    {selectedLead.lead_description}
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-center text-muted py-5">
                              No lead selected
                            </div>
                          )}
                        </Modal.Body>

                        {/* ================= FOOTER ================= */}
                        <Modal.Footer className="border-top">
                          <Button
                            variant="outline-secondary"
                            onClick={() => setIsLeadModalOpen(false)}
                          >
                            Close
                          </Button>

                          {/* <Button
                            variant="primary"
                            onClick={() =>
                              navigate(
                                `/leads/details?lead_id=${encodeURIComponent(
                                  selectedLead?.lead_id || "",
                                )}`,
                              )
                            }
                          >
                            Open Full Lead
                          </Button> */}
                        </Modal.Footer>
                      </Modal>
                    </Card.Body>
                  </Card>

                  {/* Deals Section - Enhanced */}
                  <Card className="mb-5 shadow border-0 rounded-3">
                    <Card.Header className="bg-warning text-dark d-flex align-items-center rounded-top">
                      <FaHandshake className="me-2" size={20} /> Deals Overview{" "}
                      {renderTooltip(
                        "Insights into deals, priorities, and closed values",
                      )}
                    </Card.Header>
                    <Card.Body className="p-4">
                      <Row className="g-3 mb-4">
                        {/* ================= TOTAL DEALS ================= */}
                        <Col xs={12} sm={4}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 text-center dashboard-card">
                            <Card.Body className="p-4">
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="bg-warning-subtle rounded-circle p-3">
                                  <FaHandshake
                                    size={22}
                                    className="text-warning"
                                  />
                                </div>
                                <span className="badge bg-warning-subtle text-warning fs-11">
                                  Total
                                </span>
                              </div>

                              <small className="text-muted fw-medium">
                                Total Deals
                              </small>

                              <h3 className="fw-bold text-warning mt-1 mb-0">
                                {totalDeals.toLocaleString()}
                              </h3>
                            </Card.Body>
                          </Card>
                        </Col>

                        {/* ================= CLOSED (7 DAYS) ================= */}
                        <Col xs={12} sm={4}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 text-center dashboard-card">
                            <Card.Body className="p-4">
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="bg-success-subtle rounded-circle p-3">
                                  <FaHandHoldingUsd
                                    size={22}
                                    className="text-success"
                                  />
                                </div>

                                {/* 🔁 Replace with real growth later */}
                                <span className="badge bg-success-subtle text-success fs-11">
                                  Last 7 Days
                                </span>
                              </div>

                              <small className="text-muted fw-medium">
                                Closed Value
                              </small>

                              <h3 className="fw-bold text-success mt-1 mb-0">
                                $
                                {calculateClosedValue(
                                  "last7days",
                                ).toLocaleString()}
                              </h3>
                            </Card.Body>
                          </Card>
                        </Col>

                        {/* ================= CLOSED (3 MONTHS) ================= */}
                        <Col xs={12} sm={4}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 text-center dashboard-card">
                            <Card.Body className="p-4">
                              <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="bg-primary-subtle rounded-circle p-3">
                                  <FaHandHoldingUsd
                                    size={22}
                                    className="text-primary"
                                  />
                                </div>

                                <span className="badge bg-primary-subtle text-primary fs-11">
                                  Last 3 Months
                                </span>
                              </div>

                              <small className="text-muted fw-medium">
                                Closed Value
                              </small>

                              <h3 className="fw-bold text-primary mt-1 mb-0">
                                $
                                {calculateClosedValue(
                                  "last3months",
                                ).toLocaleString()}
                              </h3>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      <Row className="g-4 mb-4">
                        <Col md={4}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* ================= HEADER ================= */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-warning-subtle rounded-circle p-2 me-3">
                                  <FaChartPie
                                    className="text-warning"
                                    size={18}
                                  />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Deal Status Distribution
                                  </h6>
                                  <small className="text-muted">
                                    How deals are progressing
                                  </small>
                                </div>
                              </div>

                              <Tip text="Distribution of deals across different statuses">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* ================= BODY ================= */}
                            <Card.Body className="p-4">
                              {dealStatusData.length > 0 ? (
                                <>
                                  {/* ===== KPI STRIP ===== */}
                                  <div className="d-flex justify-content-between mb-3 text-center">
                                    <div className="flex-fill">
                                      <div className="small text-warning fw-medium">
                                        Total Statuses
                                      </div>
                                      <div className="fw-bold text-dark">
                                        {dealStatusData.length}
                                      </div>
                                    </div>

                                    <div className="flex-fill border-start border-end">
                                      <div className="small text-success fw-medium">
                                        Top Status
                                      </div>
                                      <div className="fw-bold text-dark text-truncate px-2">
                                        {highDealStatuses[0] || "—"}
                                      </div>
                                    </div>

                                    <div className="flex-fill">
                                      <div className="small text-danger fw-medium">
                                        Least Used
                                      </div>
                                      <div className="fw-bold text-dark text-truncate px-2">
                                        {lowDealStatuses[0] || "—"}
                                      </div>
                                    </div>
                                  </div>

                                  {/* ===== CHART ===== */}
                                  <div
                                    className="position-relative"
                                    style={{ height: 220 }}
                                  >
                                    <ResponsiveContainer>
                                      <PieChart>
                                        <Pie
                                          data={dealStatusData}
                                          dataKey="value"
                                          nameKey="name"
                                          cx="50%"
                                          cy="50%"
                                          innerRadius={55}
                                          outerRadius={90}
                                          paddingAngle={3}
                                          labelLine={false}
                                        >
                                          {dealStatusData.map((_, index) => (
                                            <Cell
                                              key={index}
                                              fill={
                                                COLORS[index % COLORS.length]
                                              }
                                              stroke="#fff"
                                              strokeWidth={2}
                                            />
                                          ))}
                                        </Pie>

                                        <RechartsTooltip
                                          content={customChartTooltip}
                                          cursor={{
                                            fill: "rgba(245,158,11,0.08)",
                                          }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>
                                  </div>

                                  {/* ===== RANKED LIST ===== */}
                                  <div className="mt-4">
                                    {dealStatusData
                                      .sort((a, b) => b.value - a.value)
                                      .slice(0, 4)
                                      .map((d, index) => (
                                        <div
                                          key={d.name}
                                          className="d-flex justify-content-between align-items-center small mb-2 ranked-row"
                                        >
                                          <div className="d-flex align-items-center gap-2">
                                            <span
                                              className="rounded-circle"
                                              style={{
                                                width: 10,
                                                height: 10,
                                                backgroundColor:
                                                  COLORS[index % COLORS.length],
                                              }}
                                            />
                                            <span className="fw-medium text-dark">
                                              {d.name}
                                            </span>
                                          </div>
                                          <span className="text-muted">
                                            {d.value}
                                          </span>
                                        </div>
                                      ))}
                                  </div>
                                </>
                              ) : (
                                /* ===== EMPTY STATE ===== */
                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                                  <FaChartPie
                                    size={48}
                                    className="opacity-25 mb-3"
                                  />
                                  <h6 className="mb-1 fw-medium text-dark">
                                    No deal status data
                                  </h6>
                                  <p className="mb-0 small">
                                    Status insights will appear once deals are
                                    added
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col md={4}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* ================= HEADER ================= */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-warning-subtle rounded-circle p-2 me-3">
                                  <FaChartBar
                                    className="text-warning"
                                    size={18}
                                  />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Priority Distribution
                                  </h6>
                                  <small className="text-muted">
                                    Deal urgency breakdown
                                  </small>
                                </div>
                              </div>

                              <Tip text="Distribution of deals by priority level">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* ================= BODY ================= */}
                            <Card.Body className="p-4">
                              {priorityData.length > 0 ? (
                                <>
                                  {/* ===== KPI STRIP ===== */}
                                  <div className="d-flex justify-content-between mb-3 text-center">
                                    <div className="flex-fill">
                                      <div className="small text-warning fw-medium">
                                        Priorities Used
                                      </div>
                                      <div className="fw-bold text-dark">
                                        {priorityData.length}
                                      </div>
                                    </div>

                                    <div className="flex-fill border-start border-end">
                                      <div className="small text-danger fw-medium">
                                        Highest Load
                                      </div>
                                      <div className="fw-bold text-dark text-truncate px-2">
                                        {priorityData[0]?.name || "—"}
                                      </div>
                                    </div>

                                    <div className="flex-fill">
                                      <div className="small text-success fw-medium">
                                        Lowest Load
                                      </div>
                                      <div className="fw-bold text-dark text-truncate px-2">
                                        {priorityData[priorityData.length - 1]
                                          ?.name || "—"}
                                      </div>
                                    </div>
                                  </div>

                                  {/* ===== CHART ===== */}
                                  <div
                                    className="position-relative"
                                    style={{ height: 220, overflowX: "auto" }}
                                  >
                                    <ResponsiveContainer
                                      minWidth={priorityData.length * 60}
                                    >
                                      <BarChart data={priorityData}>
                                        <CartesianGrid
                                          strokeDasharray="3 3"
                                          vertical={false}
                                        />
                                        <XAxis
                                          dataKey="name"
                                          tick={{ fontSize: 11 }}
                                          angle={-30}
                                          textAnchor="end"
                                          interval={0}
                                          height={60}
                                        />
                                        <YAxis hide />
                                        <RechartsTooltip
                                          content={customChartTooltip}
                                        />
                                        <Bar
                                          dataKey="value"
                                          fill="#f59e0b"
                                          radius={[6, 6, 0, 0]}
                                          barSize={26}
                                        />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </div>

                                  {/* ===== RANKED LIST ===== */}
                                  <div className="mt-4">
                                    {priorityData
                                      .sort((a, b) => b.value - a.value)
                                      .slice(0, 4)
                                      .map((p) => (
                                        <div
                                          key={p.name}
                                          className="d-flex justify-content-between align-items-center small mb-2 ranked-row"
                                        >
                                          <span className="fw-medium text-dark">
                                            {p.name}
                                          </span>
                                          <span className="text-muted">
                                            {p.value}
                                          </span>
                                        </div>
                                      ))}
                                  </div>
                                </>
                              ) : (
                                /* ===== EMPTY STATE ===== */
                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                                  <FaChartBar
                                    size={48}
                                    className="opacity-25 mb-3"
                                  />
                                  <h6 className="mb-1 fw-medium text-dark">
                                    No priority data
                                  </h6>
                                  <p className="mb-0 small">
                                    Priority insights will appear once deals are
                                    added
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col md={4}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* ================= HEADER ================= */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-warning-subtle rounded-circle p-2 me-3">
                                  <FaChartPie
                                    className="text-warning"
                                    size={18}
                                  />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Source Distribution
                                  </h6>
                                  <small className="text-muted">
                                    Where deals are coming from
                                  </small>
                                </div>
                              </div>

                              <Tip text="Distribution of deals grouped by lead source">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* ================= BODY ================= */}
                            <Card.Body className="p-4">
                              {dealSourceData.length > 0 ? (
                                <>
                                  {/* ===== KPI STRIP ===== */}
                                  <div className="d-flex justify-content-between mb-3 text-center">
                                    <div className="flex-fill">
                                      <div className="small text-warning fw-medium">
                                        Sources Used
                                      </div>
                                      <div className="fw-bold text-dark">
                                        {dealSourceData.length}
                                      </div>
                                    </div>

                                    <div className="flex-fill border-start border-end">
                                      <div className="small text-success fw-medium">
                                        Top Source
                                      </div>
                                      <div className="fw-bold text-dark text-truncate px-2">
                                        {highDealSources[0] || "—"}
                                      </div>
                                    </div>

                                    <div className="flex-fill">
                                      <div className="small text-muted fw-medium">
                                        Lowest Source
                                      </div>
                                      <div className="fw-bold text-dark text-truncate px-2">
                                        {lowDealSources[0] || "—"}
                                      </div>
                                    </div>
                                  </div>

                                  {/* ===== CHART ===== */}
                                  <div style={{ height: 220 }}>
                                    <ResponsiveContainer>
                                      <PieChart>
                                        <Pie
                                          data={dealSourceData}
                                          dataKey="value"
                                          nameKey="name"
                                          cx="50%"
                                          cy="50%"
                                          innerRadius={45}
                                          outerRadius={85}
                                          paddingAngle={3}
                                          labelLine={false}
                                          isAnimationActive
                                          animationDuration={700}
                                        >
                                          {dealSourceData.map((_, index) => (
                                            <Cell
                                              key={index}
                                              fill={
                                                COLORS[index % COLORS.length]
                                              }
                                              stroke="#fff"
                                              strokeWidth={2}
                                            />
                                          ))}
                                        </Pie>

                                        <RechartsTooltip
                                          content={customChartTooltip}
                                          cursor={{
                                            fill: "rgba(245,158,11,0.08)",
                                          }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>
                                  </div>

                                  {/* ===== RANKED LIST ===== */}
                                  <div className="mt-4">
                                    {dealSourceData
                                      .sort((a, b) => b.value - a.value)
                                      .slice(0, 4)
                                      .map((d, index) => (
                                        <div
                                          key={d.name}
                                          className="d-flex justify-content-between align-items-center small mb-2 ranked-row"
                                        >
                                          <span className="fw-medium text-dark">
                                            {d.name}
                                          </span>
                                          <span className="text-muted">
                                            {d.value}
                                          </span>
                                        </div>
                                      ))}
                                  </div>
                                </>
                              ) : (
                                /* ===== EMPTY STATE ===== */
                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                                  <FaChartPie
                                    size={48}
                                    className="opacity-25 mb-3"
                                  />
                                  <h6 className="mb-1 fw-medium text-dark">
                                    No source data
                                  </h6>
                                  <p className="mb-0 small">
                                    Deal sources will appear once deals are
                                    created
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                        {/* ================= HEADER ================= */}
                        <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <div className="bg-warning-subtle rounded-circle p-2 me-3">
                              <FaList className="text-warning" size={20} />
                            </div>
                            <div>
                              <h6 className="mb-0 fw-semibold text-dark">
                                Recent Deals
                              </h6>
                              <small className="text-muted">
                                Latest deal activity
                              </small>
                            </div>
                          </div>

                          <Tip text="Most recently created deals — click a row to view full deal details">
                            <i className="fa fa-info-circle text-muted fs-14" />
                          </Tip>
                        </Card.Header>

                        {/* ================= BODY ================= */}
                        <Card.Body className="p-0">
                          <div className="table-responsive">
                            <Table
                              hover
                              className="mb-0 table-borderless recent-deals-table"
                            >
                              <thead className="bg-light-subtle">
                                <tr>
                                  <th className="ps-4 py-3 fw-semibold">#</th>
                                  <th className="py-3 fw-semibold">Deal ID</th>
                                  <th className="py-3 fw-semibold">Value</th>
                                  <th className="py-3 fw-semibold">Status</th>
                                  <th className="py-3 fw-semibold">Priority</th>
                                  <th className="py-3 fw-semibold">Source</th>
                                  <th className="py-3 fw-semibold text-center">
                                    Created
                                  </th>
                                  <th className="py-3 fw-semibold text-center">
                                    Action
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {recentDeals.slice(0, 10).map((deal, index) => (
                                  <tr
                                    key={deal.deal_id}
                                    className="align-middle deal-row"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      setSelectedDeal(deal);
                                      setIsDealModalOpen(true);
                                    }}
                                  >
                                    <td className="ps-4 py-3 text-muted">
                                      {index + 1}
                                    </td>

                                    <td
                                      className="py-3 fw-medium text-truncate"
                                      style={{ maxWidth: 120 }}
                                    >
                                      {deal.deal_id}
                                    </td>

                                    <td className="py-3 fw-semibold text-dark">
                                      ₹
                                      {Number(
                                        deal.deal_value || 0,
                                      ).toLocaleString()}
                                    </td>

                                    <td className="py-3">
                                      <Badge
                                        pill
                                        className={
                                          deal.status === "Won"
                                            ? "bg-success-subtle text-success"
                                            : deal.status === "Lost"
                                              ? "bg-danger-subtle text-danger"
                                              : "bg-primary-subtle text-primary"
                                        }
                                      >
                                        {deal.status}
                                      </Badge>
                                    </td>

                                    <td className="py-3">
                                      <Badge
                                        pill
                                        className={
                                          deal.deal_priority === "High"
                                            ? "bg-danger-subtle text-danger"
                                            : deal.deal_priority === "Medium"
                                              ? "bg-warning-subtle text-warning"
                                              : "bg-secondary-subtle text-secondary"
                                        }
                                      >
                                        {deal.deal_priority || "—"}
                                      </Badge>
                                    </td>

                                    <td
                                      className="py-3 text-muted text-truncate"
                                      style={{ maxWidth: 140 }}
                                    >
                                      {deal.dealsource?.deal_source_nema ||
                                        "Unknown"}
                                    </td>

                                    <td className="py-3 text-muted text-center">
                                      {deal.created_at
                                        ? moment(deal.created_at).format(
                                            "MMM DD, YYYY",
                                          )
                                        : "—"}
                                    </td>

                                    <td className="py-3 text-center">
                                      <Button
                                        variant="outline-warning"
                                        size="sm"
                                        className="rounded-pill px-3"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedDeal(deal);
                                          setIsDealModalOpen(true);
                                        }}
                                      >
                                        View
                                      </Button>
                                    </td>
                                  </tr>
                                ))}

                                {/* ===== EMPTY STATE ===== */}
                                {recentDeals.length === 0 && (
                                  <tr>
                                    <td
                                      colSpan={8}
                                      className="text-center text-muted py-5"
                                    >
                                      <FaHandshake
                                        size={48}
                                        className="opacity-25 mb-3"
                                      />
                                      <h6 className="mb-1 fw-medium text-dark">
                                        No recent deals found
                                      </h6>
                                      <p className="mb-0 small">
                                        Newly created deals will appear here
                                      </p>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </div>
                        </Card.Body>

                        {/* ================= FOOTER ================= */}
                        {recentDeals.length > 0 && (
                          <div className="px-4 py-3 bg-light border-top text-end small text-muted">
                            Showing {Math.min(10, recentDeals.length)} of{" "}
                            {recentDeals.length} deals
                          </div>
                        )}
                      </Card>
                    </Card.Body>
                  </Card>
                  <Modal
                    show={isDealModalOpen}
                    onHide={() => setIsDealModalOpen(false)}
                    centered
                    size="lg"
                  >
                    {/* ================= HEADER ================= */}
                    <Modal.Header closeButton className="border-bottom">
                      <div className="d-flex align-items-center gap-3">
                        <div className="bg-warning-subtle rounded-circle p-2">
                          <FaHandshake className="text-warning" size={20} />
                        </div>
                        <div>
                          <Modal.Title className="fw-semibold">
                            Deal Details
                          </Modal.Title>
                          <small className="text-muted">
                            {selectedDeal?.deal_id || "—"}
                          </small>
                        </div>
                      </div>
                    </Modal.Header>

                    {/* ================= BODY ================= */}
                    <Modal.Body className="p-4">
                      {selectedDeal ? (
                        <>
                          {/* ================= KPI STRIP ================= */}
                          <Row className="g-3 mb-4">
                            <Col md={4}>
                              <div className="p-3 rounded-3 bg-success-subtle text-center">
                                <div className="small text-success fw-medium">
                                  Deal Value
                                </div>
                                <div className="fw-bold text-dark">
                                  {selectedDeal.currency}{" "}
                                  {Number(
                                    selectedDeal.deal_value || 0,
                                  ).toLocaleString()}
                                </div>
                              </div>
                            </Col>

                            <Col md={4}>
                              <div className="p-3 rounded-3 bg-primary-subtle text-center">
                                <div className="small text-primary fw-medium">
                                  Status
                                </div>
                                <div className="fw-bold text-dark">
                                  {selectedDeal.status}
                                </div>
                              </div>
                            </Col>

                            <Col md={4}>
                              <div className="p-3 rounded-3 bg-warning-subtle text-center">
                                <div className="small text-warning fw-medium">
                                  Priority
                                </div>
                                <div className="fw-bold text-dark">
                                  {selectedDeal.deal_priority}
                                </div>
                              </div>
                            </Col>
                          </Row>

                          {/* ================= DETAILS GRID ================= */}
                          <Row className="g-4">
                            {/* LEFT */}
                            <Col md={6}>
                              <div className="mb-3">
                                <small className="text-muted">
                                  Deal Source
                                </small>
                                <div className="fw-medium">
                                  {selectedDeal.dealsource?.deal_source_nema ||
                                    "—"}
                                </div>
                              </div>

                              <div className="mb-3">
                                <small className="text-muted">Created On</small>
                                <div className="fw-medium">
                                  {moment(selectedDeal.created_at).format(
                                    "MMM DD, YYYY",
                                  )}
                                </div>
                              </div>

                              <div className="mb-3">
                                <small className="text-muted">Due Date</small>
                                <div className="fw-medium">
                                  {selectedDeal.due_date
                                    ? moment(selectedDeal.due_date).format(
                                        "MMM DD, YYYY",
                                      )
                                    : "—"}
                                </div>
                              </div>

                              <div className="mb-3">
                                <small className="text-muted">
                                  Follow-up Date
                                </small>
                                <div className="fw-medium">
                                  {selectedDeal.follow_up_date
                                    ? moment(
                                        selectedDeal.follow_up_date,
                                      ).format("MMM DD, YYYY")
                                    : "—"}
                                </div>
                              </div>
                            </Col>

                            {/* RIGHT */}
                            <Col md={6}>
                              <div className="mb-3">
                                <small className="text-muted">Company</small>
                                <div className="fw-medium">
                                  {selectedDeal.contact?.company_name || "—"}
                                </div>
                              </div>

                              <div className="mb-3">
                                <small className="text-muted">
                                  Contact Person
                                </small>
                                <div className="fw-medium">
                                  {selectedDeal.contact?.contact_person_name ||
                                    "—"}
                                </div>
                              </div>

                              <div className="mb-3">
                                <small className="text-muted">
                                  Expected Close
                                </small>
                                <div className="fw-medium">
                                  {selectedDeal.expected_closing_date
                                    ? moment(
                                        selectedDeal.expected_closing_date,
                                      ).format("MMM DD, YYYY")
                                    : "—"}
                                </div>
                              </div>
                            </Col>
                          </Row>

                          {/* ================= PRODUCT & PRICING ================= */}
                          {selectedDeal.leads && (
                            <div className="mt-4 p-3 rounded-3 bg-light">
                              <div className="fw-semibold mb-2">
                                Product & Pricing
                              </div>

                              <Row className="small">
                                <Col md={4}>
                                  <div className="text-muted">Product</div>
                                  <div className="fw-medium">
                                    {selectedDeal.product?.product_name || "—"}
                                  </div>
                                </Col>

                                <Col md={4}>
                                  <div className="text-muted">Logins</div>
                                  <div className="fw-medium">
                                    {selectedDeal.leads.no_logins}
                                  </div>
                                </Col>

                                <Col md={4}>
                                  <div className="text-muted">Rate / Login</div>
                                  <div className="fw-medium">
                                    {selectedDeal.currency}{" "}
                                    {selectedDeal.leads.rate_per_login}
                                  </div>
                                </Col>
                              </Row>

                              <hr className="my-2" />

                              <Row className="small">
                                <Col md={3}>
                                  <div className="text-muted">Amount</div>
                                  <div className="fw-medium">
                                    {selectedDeal.currency}{" "}
                                    {selectedDeal.leads.amount}
                                  </div>
                                </Col>

                                <Col md={3}>
                                  <div className="text-muted">Discount</div>
                                  <div className="fw-medium text-danger">
                                    {
                                      selectedDeal.leads
                                        .proposal_discount_percentage
                                    }
                                    %
                                  </div>
                                </Col>

                                <Col md={3}>
                                  <div className="text-muted">Tax</div>
                                  <div className="fw-medium">
                                    {selectedDeal.leads.proposal_tax_percentage}
                                    %
                                  </div>
                                </Col>

                                <Col md={3}>
                                  <div className="text-muted">Total</div>
                                  <div className="fw-bold">
                                    {selectedDeal.currency}{" "}
                                    {selectedDeal.leads.total_amount}
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          )}

                          {/* ================= TAX SPLIT ================= */}
                          {selectedDeal.leads && (
                            <div className="mt-3 p-3 rounded-3 bg-white border">
                              <div className="fw-semibold mb-2">
                                Tax Breakdown
                              </div>
                              <Row className="small">
                                <Col md={4}>
                                  CGST ({selectedDeal.leads.cgst_percentage}%):{" "}
                                  <strong>{selectedDeal.leads.cgst}</strong>
                                </Col>
                                <Col md={4}>
                                  SGST ({selectedDeal.leads.sgst_percentage}%):{" "}
                                  <strong>{selectedDeal.leads.sgst}</strong>
                                </Col>
                                <Col md={4}>
                                  IGST ({selectedDeal.leads.igst_percentage}%):{" "}
                                  <strong>{selectedDeal.leads.igst}</strong>
                                </Col>
                              </Row>
                            </div>
                          )}

                          {/* ================= DESCRIPTION ================= */}
                          {selectedDeal.Description && (
                            <div className="mt-4">
                              <small className="text-muted">Description</small>
                              <div className="p-3 rounded-3 bg-light">
                                {selectedDeal.Description}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center text-muted py-5">
                          No deal selected
                        </div>
                      )}
                    </Modal.Body>

                    {/* ================= FOOTER ================= */}
                    <Modal.Footer className="border-top">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setIsDealModalOpen(false)}
                      >
                        Close
                      </Button>

                      {/* <Button
                        variant="warning"
                        onClick={() =>
                          navigate(
                            `/deals/details?deal_id=${encodeURIComponent(
                              selectedDeal?.deal_id || "",
                            )}`,
                          )
                        }
                      >
                        Open Full Deal
                      </Button> */}
                    </Modal.Footer>
                  </Modal>

                  {/* Proposals Section - Enhanced */}
                  <Card className="mb-5 shadow border-0 rounded-3">
                    <Card.Header className="bg-info text-white d-flex align-items-center rounded-top">
                      <FaFileAlt className="me-2" size={20} /> Proposals
                      Overview{" "}
                      {renderTooltip(
                        "Status, top products, and upcoming follow-ups",
                      )}
                    </Card.Header>
                    <Card.Body className="p-4">
                      <Row className="g-4 mb-4">
                        <Col md={4}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* ================= HEADER ================= */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-info-subtle rounded-circle p-2 me-3">
                                  <FaChartPie className="text-info" size={18} />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Proposal Status
                                  </h6>
                                  <small className="text-muted">
                                    Current proposal progress
                                  </small>
                                </div>
                              </div>

                              <Tip text="Distribution of proposals by their current status">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* ================= BODY ================= */}
                            <Card.Body className="p-4">
                              {proposalStatusData.length > 0 ? (
                                <>
                                  {/* ===== KPI ===== */}
                                  <div className="mb-3 text-center">
                                    <div className="small text-info fw-medium">
                                      Total Status Types
                                    </div>
                                    <div className="fw-bold text-dark">
                                      {proposalStatusData.length}
                                    </div>
                                  </div>

                                  {/* ===== CHART ===== */}
                                  <div style={{ height: 220 }}>
                                    <ResponsiveContainer>
                                      <PieChart>
                                        <Pie
                                          data={proposalStatusData}
                                          dataKey="value"
                                          nameKey="name"
                                          cx="50%"
                                          cy="50%"
                                          innerRadius={45}
                                          outerRadius={85}
                                          paddingAngle={3}
                                          labelLine={false}
                                        >
                                          {proposalStatusData.map(
                                            (_, index) => (
                                              <Cell
                                                key={index}
                                                fill={
                                                  COLORS[index % COLORS.length]
                                                }
                                                stroke="#fff"
                                                strokeWidth={2}
                                              />
                                            ),
                                          )}
                                        </Pie>

                                        <RechartsTooltip
                                          content={customChartTooltip}
                                          cursor={{
                                            fill: "rgba(59,130,246,0.08)",
                                          }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>
                                  </div>

                                  {/* ===== RANKED LIST ===== */}
                                  <div className="mt-4">
                                    {proposalStatusData.slice(0, 3).map((d) => (
                                      <div
                                        key={d.name}
                                        className="d-flex justify-content-between align-items-center small mb-2 ranked-row"
                                      >
                                        <span className="fw-medium text-dark">
                                          {d.name}
                                        </span>
                                        <span className="text-muted">
                                          {d.value}
                                        </span>
                                      </div>
                                    ))}
                                  </div>

                                  {/* ===== INSIGHT ===== */}
                                  <div className="text-center mt-3 small text-muted">
                                    <strong>Top Status:</strong>{" "}
                                    {highProposalStatuses[0] || "N/A"} <br />
                                    <strong>Lowest:</strong>{" "}
                                    {lowProposalStatuses[0] || "N/A"}
                                  </div>
                                </>
                              ) : (
                                /* ===== EMPTY STATE ===== */
                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                                  <FaChartPie
                                    size={48}
                                    className="opacity-25 mb-3"
                                  />
                                  <h6 className="mb-1 fw-medium text-dark">
                                    No proposal status data
                                  </h6>
                                  <p className="mb-0 small">
                                    Proposal statuses will appear once data is
                                    available
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col md={4}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* ================= HEADER ================= */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-info-subtle rounded-circle p-2 me-3">
                                  <FaChartPie className="text-info" size={18} />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Proposal Status
                                  </h6>
                                  <small className="text-muted">
                                    Current proposal progress
                                  </small>
                                </div>
                              </div>

                              <Tip text="Distribution of proposals by their current status">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* ================= BODY ================= */}
                            <Card.Body className="p-4">
                              {proposalStatusData.length > 0 ? (
                                <>
                                  {/* ===== KPI ===== */}
                                  <div className="mb-3 text-center">
                                    <div className="small text-info fw-medium">
                                      Total Status Types
                                    </div>
                                    <div className="fw-bold text-dark">
                                      {proposalStatusData.length}
                                    </div>
                                  </div>

                                  {/* ===== CHART ===== */}
                                  <div style={{ height: 220 }}>
                                    <ResponsiveContainer>
                                      <PieChart>
                                        <Pie
                                          data={proposalStatusData}
                                          dataKey="value"
                                          nameKey="name"
                                          cx="50%"
                                          cy="50%"
                                          innerRadius={45}
                                          outerRadius={85}
                                          paddingAngle={3}
                                          labelLine={false}
                                        >
                                          {proposalStatusData.map(
                                            (_, index) => (
                                              <Cell
                                                key={index}
                                                fill={
                                                  COLORS[index % COLORS.length]
                                                }
                                                stroke="#fff"
                                                strokeWidth={2}
                                              />
                                            ),
                                          )}
                                        </Pie>

                                        <RechartsTooltip
                                          content={customChartTooltip}
                                          cursor={{
                                            fill: "rgba(59,130,246,0.08)",
                                          }}
                                        />
                                      </PieChart>
                                    </ResponsiveContainer>
                                  </div>

                                  {/* ===== RANKED LIST ===== */}
                                  <div className="mt-4">
                                    {proposalStatusData.slice(0, 3).map((d) => (
                                      <div
                                        key={d.name}
                                        className="d-flex justify-content-between align-items-center small mb-2 ranked-row"
                                      >
                                        <span className="fw-medium text-dark">
                                          {d.name}
                                        </span>
                                        <span className="text-muted">
                                          {d.value}
                                        </span>
                                      </div>
                                    ))}
                                  </div>

                                  {/* ===== INSIGHT ===== */}
                                  <div className="text-center mt-3 small text-muted">
                                    <strong>Top Status:</strong>{" "}
                                    {highProposalStatuses[0] || "N/A"} <br />
                                    <strong>Lowest:</strong>{" "}
                                    {lowProposalStatuses[0] || "N/A"}
                                  </div>
                                </>
                              ) : (
                                /* ===== EMPTY STATE ===== */
                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                                  <FaChartPie
                                    size={48}
                                    className="opacity-25 mb-3"
                                  />
                                  <h6 className="mb-1 fw-medium text-dark">
                                    No proposal status data
                                  </h6>
                                  <p className="mb-0 small">
                                    Proposal statuses will appear once data is
                                    available
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>

                        <Col md={4}>
                          <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                            {/* ================= HEADER ================= */}
                            <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                <div className="bg-info-subtle rounded-circle p-2 me-3">
                                  <FaList className="text-info" size={18} />
                                </div>
                                <div>
                                  <h6 className="mb-0 fw-semibold text-dark">
                                    Upcoming Validations
                                  </h6>
                                  <small className="text-muted">
                                    Proposals nearing validation
                                  </small>
                                </div>
                              </div>

                              <Tip text="Upcoming proposal validation dates">
                                <i className="fa fa-info-circle text-muted fs-14" />
                              </Tip>
                            </Card.Header>

                            {/* ================= BODY ================= */}
                            <Card.Body className="p-4">
                              {upcomingFollowups.length > 0 ? (
                                <>
                                  {/* ===== KPI ===== */}
                                  <div className="mb-3 text-center">
                                    <div className="small text-info fw-medium">
                                      Upcoming Count
                                    </div>
                                    <div className="fw-bold text-dark">
                                      {upcomingFollowups.length}
                                    </div>
                                  </div>

                                  {/* ===== LIST ===== */}
                                  <div>
                                    {upcomingFollowups
                                      .slice(0, 5)
                                      .map((prop, index) => {
                                        const daysLeft = Math.ceil(
                                          (new Date(
                                            prop.validation_date,
                                          ).getTime() -
                                            Date.now()) /
                                            (1000 * 60 * 60 * 24),
                                        );

                                        return (
                                          <div
                                            key={prop.proposal_id}
                                            className="d-flex justify-content-between align-items-center py-2 px-2 mb-2 rounded-3 ranked-row"
                                            style={{
                                              background:
                                                "rgba(59,130,246,0.04)",
                                            }}
                                          >
                                            {/* Left */}
                                            <div className="d-flex align-items-center gap-2">
                                              <span
                                                className="rounded-circle d-flex align-items-center justify-content-center fw-semibold text-info"
                                                style={{
                                                  width: 26,
                                                  height: 26,
                                                  background:
                                                    "rgba(59,130,246,0.15)",
                                                  fontSize: 12,
                                                }}
                                              >
                                                {index + 1}
                                              </span>

                                              <span
                                                className="fw-medium text-dark text-truncate"
                                                style={{ maxWidth: 120 }}
                                                title={prop.proposal_id}
                                              >
                                                {prop.proposal_id}
                                              </span>
                                            </div>

                                            {/* Right */}
                                            <div className="text-end">
                                              <div className="small text-muted">
                                                {moment(
                                                  prop.validation_date,
                                                ).format("MMM DD")}
                                              </div>

                                              <Badge
                                                pill
                                                bg={
                                                  daysLeft <= 3
                                                    ? "danger-subtle"
                                                    : daysLeft <= 7
                                                      ? "warning-subtle"
                                                      : "info-subtle"
                                                }
                                                text={
                                                  daysLeft <= 3
                                                    ? "danger"
                                                    : daysLeft <= 7
                                                      ? "warning"
                                                      : "info"
                                                }
                                                className="fw-normal"
                                              >
                                                {daysLeft} days
                                              </Badge>
                                            </div>
                                          </div>
                                        );
                                      })}
                                  </div>

                                  {/* ===== INSIGHT ===== */}
                                  <div className="text-center mt-3 small text-muted">
                                    Next validation on{" "}
                                    <strong>
                                      {moment(
                                        upcomingFollowups[0].validation_date,
                                      ).format("MMM DD, YYYY")}
                                    </strong>
                                  </div>
                                </>
                              ) : (
                                /* ===== EMPTY STATE ===== */
                                <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                                  <FaList
                                    size={48}
                                    className="opacity-25 mb-3"
                                  />
                                  <h6 className="mb-1 fw-medium text-dark">
                                    No upcoming validations
                                  </h6>
                                  <p className="mb-0 small">
                                    Validation reminders will appear here
                                  </p>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>

                      <Card className="h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                        {/* ================= HEADER ================= */}
                        <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <div className="bg-info-subtle rounded-circle p-2 me-3">
                              <FaList className="text-info" size={18} />
                            </div>
                            <div>
                              <h6 className="mb-0 fw-semibold text-dark">
                                Recent Proposals
                              </h6>
                              <small className="text-muted">
                                Latest proposals created
                              </small>
                            </div>
                          </div>

                          <Tip text="Most recently created proposals with status and validation date">
                            <i className="fa fa-info-circle text-muted fs-14" />
                          </Tip>
                        </Card.Header>

                        {/* ================= BODY ================= */}
                        <Card.Body className="p-0">
                          {recentProposals.length > 0 ? (
                            <>
                              <div className="table-responsive">
                                <Table
                                  hover
                                  className="mb-0 table-borderless align-middle recent-contacts-table"
                                >
                                  <thead className="bg-light-subtle">
                                    <tr>
                                      <th className="ps-4 py-3 fw-semibold">
                                        #
                                      </th>
                                      <th className="py-3 fw-semibold">
                                        Proposal ID
                                      </th>
                                      <th className="py-3 fw-semibold">
                                        Status
                                      </th>
                                      <th className="py-3 fw-semibold">
                                        Created
                                      </th>
                                      <th className="py-3 fw-semibold">
                                        Validation
                                      </th>
                                      <th className="py-3 fw-semibold text-center">
                                        Action
                                      </th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {recentProposals
                                      .slice(0, 10)
                                      .map((prop, index) => {
                                        const daysLeft = Math.ceil(
                                          (new Date(
                                            prop.validation_date,
                                          ).getTime() -
                                            Date.now()) /
                                            (1000 * 60 * 60 * 24),
                                        );

                                        return (
                                          <tr
                                            key={prop.proposal_id}
                                            className="transaction-row"
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                              openProposalModal(prop)
                                            }
                                          >
                                            <td className="ps-4 py-3 text-muted">
                                              {index + 1}
                                            </td>

                                            <td
                                              className="py-3 fw-medium text-dark text-truncate"
                                              style={{ maxWidth: 140 }}
                                              title={prop.proposal_id}
                                            >
                                              {prop.proposal_id}
                                            </td>

                                            <td className="py-3">
                                              <Badge
                                                pill
                                                bg={
                                                  prop.status === "Accepted"
                                                    ? "success-subtle"
                                                    : prop.status === "Declined"
                                                      ? "danger-subtle"
                                                      : "warning-subtle"
                                                }
                                                text={
                                                  prop.status === "Accepted"
                                                    ? "success"
                                                    : prop.status === "Declined"
                                                      ? "danger"
                                                      : "warning"
                                                }
                                                className="fw-normal px-3 py-2"
                                              >
                                                {prop.status}
                                              </Badge>
                                            </td>

                                            <td className="py-3 text-muted">
                                              {moment(prop.created_at).format(
                                                "MMM DD, YYYY",
                                              )}
                                            </td>

                                            <td className="py-3">
                                              <div className="small text-muted">
                                                {moment(
                                                  prop.validation_date,
                                                ).format("MMM DD, YYYY")}
                                              </div>
                                              <Badge
                                                pill
                                                bg={
                                                  daysLeft <= 3
                                                    ? "danger-subtle"
                                                    : daysLeft <= 7
                                                      ? "warning-subtle"
                                                      : "info-subtle"
                                                }
                                                text={
                                                  daysLeft <= 3
                                                    ? "danger"
                                                    : daysLeft <= 7
                                                      ? "warning"
                                                      : "info"
                                                }
                                                className="fw-normal"
                                              >
                                                {daysLeft} days
                                              </Badge>
                                            </td>

                                            <td className="py-3 text-center">
                                              <Button
                                                variant="outline-info"
                                                size="sm"
                                                className="rounded-pill px-3"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  openProposalModal(prop);
                                                }}
                                              >
                                                View
                                              </Button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </Table>
                              </div>

                              {/* ================= FOOTER ================= */}
                              <div className="px-4 py-3 bg-light border-top text-end small text-muted">
                                Showing {Math.min(10, recentProposals.length)}{" "}
                                of {recentProposals.length} proposals
                              </div>
                            </>
                          ) : (
                            /* ================= EMPTY STATE ================= */
                            <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 text-muted">
                              <FaList size={48} className="opacity-25 mb-3" />
                              <h6 className="mb-1 fw-medium text-dark">
                                No recent proposals
                              </h6>
                              <p className="mb-0 small">
                                Newly created proposals will appear here
                              </p>
                            </div>
                          )}
                        </Card.Body>
                        <Modal
                          show={showProposalModal}
                          onHide={closeProposalModal}
                          centered
                          size="lg"
                        >
                          <Modal.Header closeButton className="border-bottom">
                            <Modal.Title className="fw-semibold">
                              Proposal Details
                            </Modal.Title>
                          </Modal.Header>

                          <Modal.Body>
                            {selectedProposal ? (
                              <Row className="g-4">
                                {/* ================= BASIC INFO ================= */}
                                <Col md={6}>
                                  <div className="mb-3">
                                    <small className="text-muted">
                                      Proposal ID
                                    </small>
                                    <div className="fw-semibold">
                                      {selectedProposal.proposal_id}
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <small className="text-muted">
                                      Client Proposal No
                                    </small>
                                    <div className="fw-medium">
                                      {selectedProposal.client_proposal_number ||
                                        "—"}
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <small className="text-muted">Status</small>
                                    <div>
                                      <Badge
                                        pill
                                        bg={
                                          selectedProposal.status === "Accepted"
                                            ? "success-subtle"
                                            : selectedProposal.status ===
                                                "Declined"
                                              ? "danger-subtle"
                                              : "warning-subtle"
                                        }
                                        text={
                                          selectedProposal.status === "Accepted"
                                            ? "success"
                                            : selectedProposal.status ===
                                                "Declined"
                                              ? "danger"
                                              : "warning"
                                        }
                                      >
                                        {selectedProposal.status}
                                      </Badge>
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <small className="text-muted">
                                      Created On
                                    </small>
                                    <div className="fw-medium">
                                      {moment(
                                        selectedProposal.created_at,
                                      ).format("MMM DD, YYYY")}
                                    </div>
                                  </div>
                                </Col>

                                {/* ================= VALIDITY & META ================= */}
                                <Col md={6}>
                                  <div className="mb-3">
                                    <small className="text-muted">
                                      Validation Date
                                    </small>
                                    <div className="fw-medium">
                                      {moment(
                                        selectedProposal.validation_date,
                                      ).format("MMM DD, YYYY")}
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <small className="text-muted">
                                      Days Remaining
                                    </small>
                                    <div>
                                      {(() => {
                                        const daysLeft = Math.ceil(
                                          (new Date(
                                            selectedProposal.validation_date,
                                          ).getTime() -
                                            Date.now()) /
                                            (1000 * 60 * 60 * 24),
                                        );

                                        return (
                                          <Badge
                                            pill
                                            bg={
                                              daysLeft <= 3
                                                ? "danger-subtle"
                                                : daysLeft <= 7
                                                  ? "warning-subtle"
                                                  : "info-subtle"
                                            }
                                            text={
                                              daysLeft <= 3
                                                ? "danger"
                                                : daysLeft <= 7
                                                  ? "warning"
                                                  : "info"
                                            }
                                          >
                                            {daysLeft} days left
                                          </Badge>
                                        );
                                      })()}
                                    </div>
                                  </div>

                                  <div className="mb-3">
                                    <small className="text-muted">
                                      Revision / Quotation
                                    </small>
                                    <div className="fw-medium">
                                      Rev: {selectedProposal.revision_id || "—"}{" "}
                                      | Quote:{" "}
                                      {selectedProposal.quotaiton_id || "—"}
                                    </div>
                                  </div>
                                </Col>

                                {/* ================= CLIENT & CONTACT ================= */}
                                <Col xs={12}>
                                  <div className="p-3 rounded-3 bg-light">
                                    <div className="fw-semibold mb-2">
                                      Client & Contact
                                    </div>
                                    <Row className="small">
                                      <Col md={4}>
                                        <div className="text-muted">
                                          Company
                                        </div>
                                        <div className="fw-medium">
                                          {selectedProposal.company_name}
                                        </div>
                                      </Col>

                                      <Col md={4}>
                                        <div className="text-muted">
                                          Contact Person
                                        </div>
                                        <div className="fw-medium">
                                          {selectedProposal.contact_person_name}
                                        </div>
                                      </Col>

                                      <Col md={4}>
                                        <div className="text-muted">Phone</div>
                                        <div className="fw-medium">
                                          {selectedProposal.company_phone ||
                                            "—"}
                                        </div>
                                      </Col>
                                    </Row>
                                  </div>
                                </Col>

                                {/* ================= PRODUCT & PRICING ================= */}
                                <Col xs={12}>
                                  <div className="p-3 rounded-3 bg-white border">
                                    <div className="fw-semibold mb-2">
                                      Product & Pricing
                                    </div>

                                    <Row className="small">
                                      <Col md={4}>
                                        <div className="text-muted">
                                          Product
                                        </div>
                                        <div className="fw-medium">
                                          {selectedProposal.product_name}
                                        </div>
                                      </Col>

                                      <Col md={4}>
                                        <div className="text-muted">Logins</div>
                                        <div className="fw-medium">
                                          {selectedProposal.proposal_no_logins}
                                        </div>
                                      </Col>

                                      <Col md={4}>
                                        <div className="text-muted">
                                          Rate / Login
                                        </div>
                                        <div className="fw-medium">
                                          {selectedProposal.currency}{" "}
                                          {
                                            selectedProposal.proposal_rate_per_login
                                          }
                                        </div>
                                      </Col>
                                    </Row>

                                    <hr className="my-2" />

                                    <Row className="small">
                                      <Col md={3}>
                                        <div className="text-muted">
                                          Base Amount
                                        </div>
                                        <div className="fw-medium">
                                          {selectedProposal.currency}{" "}
                                          {selectedProposal.proposal_amount}
                                        </div>
                                      </Col>

                                      <Col md={3}>
                                        <div className="text-muted">
                                          Discount
                                        </div>
                                        <div className="fw-medium text-danger">
                                          -
                                          {
                                            selectedProposal.proposal_discount_percentage
                                          }
                                          %
                                        </div>
                                      </Col>

                                      <Col md={3}>
                                        <div className="text-muted">Tax</div>
                                        <div className="fw-medium">
                                          {
                                            selectedProposal.proposal_tax_percentage
                                          }
                                          %
                                        </div>
                                      </Col>

                                      <Col md={3}>
                                        <div className="text-muted">Total</div>
                                        <div className="fw-bold">
                                          {selectedProposal.currency}{" "}
                                          {
                                            selectedProposal.proposal_total_amount
                                          }
                                        </div>
                                      </Col>
                                    </Row>
                                  </div>
                                </Col>

                                {/* ================= ADDRESS ================= */}
                                {selectedProposal.address?.length > 0 && (
                                  <Col xs={12}>
                                    <div className="p-3 rounded-3 bg-light small">
                                      <div className="fw-semibold mb-1">
                                        Address
                                      </div>
                                      <div>
                                        {selectedProposal.address[0].street},{" "}
                                        {selectedProposal.address[0].city},{" "}
                                        {selectedProposal.address[0].state},{" "}
                                        {selectedProposal.address[0].country}
                                      </div>
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            ) : (
                              <div className="text-center text-muted py-5">
                                Loading proposal details...
                              </div>
                            )}
                          </Modal.Body>

                          <Modal.Footer className="border-top">
                            <Button
                              variant="secondary"
                              onClick={closeProposalModal}
                            >
                              Close
                            </Button>

                            {/* <Button
                              variant="primary"
                              onClick={() =>
                                navigate(
                                  `/proposals/details?proposal_id=${encodeURIComponent(
                                    selectedProposal?.proposal_id,
                                  )}`,
                                )
                              }
                            >
                              Open Full Proposal
                            </Button> */}
                          </Modal.Footer>
                        </Modal>
                      </Card>
                    </Card.Body>
                  </Card>
                </>
              )}
            </Col>
          </Row>
          <Offcanvas
            show={showEmployeeSidebar}
            onHide={() => setShowEmployeeSidebar(false)}
            placement="start"
            className="employee-sidebar"
          >
            {/* ================= HEADER ================= */}
            <Offcanvas.Header closeButton className="border-bottom px-4 py-3">
              <div className="d-flex align-items-center gap-2">
                <div className="bg-primary-subtle rounded-circle p-2">
                  <FaUsers className="text-primary" />
                </div>
                <div>
                  <Offcanvas.Title className="fw-semibold mb-0">
                    Employees
                  </Offcanvas.Title>
                  <small className="text-muted">Select to view dashboard</small>
                </div>
              </div>
            </Offcanvas.Header>

            {/* ================= BODY ================= */}
            <Offcanvas.Body className="p-0 d-flex flex-column">
              {/* 🔍 Search */}
              <div className="px-3 py-3 border-bottom">
                <input
                  type="text"
                  className="form-control form-control-sm shadow-sm"
                  placeholder="Search employee…"
                  onChange={(e) =>
                    setEmployeeSearch(e.target.value.toLowerCase())
                  }
                />
              </div>

              {/* 👥 LIST */}
              <div className="flex-grow-1 overflow-auto">
                <ListGroup variant="flush">
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => {
                      const isActive = selectedUserId === user.id;
                      const initials = user.name
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase();

                      return (
                        <ListGroup.Item
                          key={user.id}
                          action
                          active={isActive}
                          onClick={() => {
                            setSelectedUserId(user.id);
                            setShowEmployeeSidebar(false);
                          }}
                          className={`d-flex align-items-center gap-3 px-4 py-3 employee-item ${
                            isActive ? "active" : ""
                          }`}
                        >
                          {/* Avatar */}
                          <div
                            className={`rounded-circle d-flex align-items-center justify-content-center fw-semibold ${
                              isActive
                                ? "bg-white text-primary"
                                : "bg-primary-subtle text-primary"
                            }`}
                            style={{ width: 36, height: 36 }}
                          >
                            {initials}
                          </div>

                          {/* Name */}
                          <div className="flex-grow-1">
                            <div className="fw-medium">{user.name}</div>
                            <small className="text-muted">{user.email}</small>
                          </div>

                          {isActive && (
                            <FaChevronRight className="text-white opacity-75" />
                          )}
                        </ListGroup.Item>
                      );
                    })
                  ) : (
                    <div className="text-center text-muted py-5">
                      <FaUser size={40} className="mb-3 opacity-25" />
                      <div>No employees found</div>
                    </div>
                  )}
                </ListGroup>

                {/* Loading */}
                {isLoading && (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                  </div>
                )}
              </div>

              {/* ================= FOOTER ================= */}
              <div className="px-4 py-3 border-top small text-muted text-center">
                Click an employee to load their dashboard
              </div>
            </Offcanvas.Body>
          </Offcanvas>
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

export default AdminDashboard;
